/**
 * Project Detail Page
 * Full project view with tabs for overview, tasks, milestones, costs, and activity
 */

import { useQueryClient } from "@tanstack/react-query";
import {
	AlertTriangle,
	ArrowLeft,
	Calendar,
	Check,
	CheckCircle2,
	ClipboardList,
	Clock,
	IndianRupee,
	Info,
	Layers,
	Pencil,
	Plus,
	Trash2,
	User,
	XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { MultiImageUpload } from "@/components/ui/image-upload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	CommentsPanel,
	projectCommentSubjectAdapter,
} from "@/features/comments";
import {
	authorizeAssignmentPayment,
	type ContractorProfile,
	confirmCompletion,
	disputeCompletion,
	fetchContractors,
	type ProjectContractorItem,
} from "@/features/marketplace/services/marketplace-api.service";
import { ProjectDirectMessagesPanel } from "@/features/projects/components/project-direct-messages-panel";
import { ProjectViewingAs } from "@/features/projects/components/project-viewing-as";
import { launchCashfreeCheckout } from "@/features/projects/services/cashfree-checkout";
import {
	type ActivityLog,
	acceptProposal,
	type CostEstimate,
	cancelProject,
	completeProject,
	createCostEstimate,
	createMilestone,
	createTask,
	deleteCostEstimate,
	deleteMilestone,
	deleteTask,
	type EscrowTransaction,
	fundProject,
	getMyPayoutAccount,
	getProjectActivity,
	getProjectById,
	getProjectContractors,
	getProjectCosts,
	getProjectMilestones,
	getProjectPayments,
	getProjectProposals,
	getProjectShortlist,
	getProjectTasks,
	hireContractor,
	inviteContractor,
	type Milestone,
	markMilestonePaid,
	type PaymentsSummary,
	type PayoutAccount,
	type Project,
	type ProjectProposalItem,
	rejectProposal,
	releaseMilestonePayment,
	removeContractor,
	removeFromShortlist,
	type ShortlistEntry,
	type Task,
	toggleProjectPublic,
	updateCostEstimate,
	updateMilestone,
	updateMilestoneStatus,
	updateProject,
	updateProjectTransformationImages,
	updateTaskStatus,
	verifyProjectFunding,
} from "@/features/projects/services/projects-api.service";
import {
	AWAITING_PAYOUT_LABEL,
	CONTRACTOR_PAYOUT_TITLE,
	isContractorAssignment,
	OVERDRAWN_ESCROW_NOTE,
} from "@/features/projects/utils/escrow-balance";
import {
	GENERAL_TASKS_LABEL,
	groupTasksByMilestone,
} from "@/features/projects/utils/group-tasks-by-milestone";
import {
	DESIGNER_RETIRED_TAB_KEYS,
	DESIGNER_TAB_KEYS,
	HOMEOWNER_TAB_IDS,
	DESIGNER_TABS as TABS,
	type DesignerTabKey as TabKey,
} from "@/features/projects/utils/project-tab-keys";
import {
	resolveTabKey,
	shouldRewriteTabKey,
} from "@/features/projects/utils/resolve-tab-key";
import { ShareAction } from "@/features/sharing/components/share-action";
import { ShareHistory } from "@/features/sharing/components/share-history";
import {
	Avatar,
	Eyebrow,
	FieldInput,
	FieldSelect,
	FloatPanel,
	ItalicAccent,
	Pill,
	type PillTone,
	Progress,
	StudioButton,
} from "@/features/studio/theme";
import { formatCalendarDate, formatPrice } from "@/lib/format";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { DesignerBoqTab } from "../components/boq";
import { DocumentsTab } from "../components/documents/documents-tab";
import { ProjectVendorRatingSection } from "../components/project-vendor-rating-section";
import {
	useProjectContractors,
	useProjectPayments,
} from "../hooks/use-projects";
import {
	getDisputeStatus,
	releasedAssignmentIdsFrom,
} from "../utils/assignment-payout";

// ============================================================================
// HELPERS
// ============================================================================

// The tab catalog of both surfaces lives in utils/project-tab-keys.ts; see
// the header there for why this one cannot own its own copy. Re-exported so
// the key lists stay importable from the surface they describe.
export { DESIGNER_RETIRED_TAB_KEYS, DESIGNER_TAB_KEYS };

// Two tabs this plan merged away carried an audience of their own, and the
// tabs that absorbed them carry a wider one: Activity became a panel of
// Overview, and the direct-message panel became half of Messages. Both
// Overview and Messages are tabs a contractor reaches, so each panel needs its
// own key here or the merge hands a contractor what its tab refused.
//
// A panel key and a tab key answer the same question -- may this reader see
// this part of the page -- so they go through one list and one predicate. A
// separate boolean per panel is what let the DM panel reach contractors in the
// first place.
type SectionKey = TabKey | "activity" | "directMessages";

// Everything a contractor may see. The four tab keys are the tabs; neither
// panel key is here, so both panels are refused. `boq` because a hired
// contractor may create, edit and send BOQs (the backend's creator roles are
// designer and hired contractor); the tab shows them the BOQ list only, not the
// designer's cost estimates.
const CONTRACTOR_SECTIONS: readonly SectionKey[] = [
	"overview",
	"plan",
	"boq",
	"messages",
];

const COST_CATEGORIES: { value: CostEstimate["category"]; labelKey: string }[] =
	[
		{ value: "design_fees", labelKey: "project.costCategoryDesignFees" },
		{ value: "labor", labelKey: "project.costCategoryLabor" },
		{ value: "materials", labelKey: "project.costCategoryMaterials" },
		{ value: "miscellaneous", labelKey: "project.costCategoryMiscellaneous" },
	];

// This renders milestone target dates, which name a day rather than a moment --
// see formatCalendarDate. The edit handler below already reads the field that
// way (`.split("T")[0]`), so formatting it in local time made the label and the
// form it opens disagree by a day for any reader west of UTC.
function formatDate(date: string): string {
	return formatCalendarDate(date);
}

// ============================================================================
// LOADING SKELETON
// ============================================================================

function ProjectDetailSkeleton(): React.ReactElement {
	return (
		<div
			className="min-h-screen py-8"
			style={{ background: "var(--color-dashboard-bg)" }}
		>
			<div className="mx-auto max-w-4xl px-4">
				<Skeleton className="mb-6 h-8 w-32" />
				<Skeleton className="mb-2 h-10 w-3/4" />
				<div className="mb-6 flex gap-3">
					<Skeleton className="h-6 w-24" />
					<Skeleton className="h-6 w-32" />
					<Skeleton className="h-6 w-28" />
				</div>
				<Skeleton className="mb-8 h-10 w-full" />
				<Skeleton className="h-64 w-full" />
			</div>
		</div>
	);
}

// ============================================================================
// OVERVIEW TAB
// ============================================================================

/**
 * An accept/reject failure, tagged with the proposal it belongs to so the
 * message can outlive the loading id that the handler clears on settle.
 */
interface ProposalActionError {
	proposalId: string;
	message: string;
}

function OverviewTab({
	project,
	proposals,
	proposalsLoading,
	proposalActionError,
	proposalActionLoadingId,
	userType,
	isSavingProjectImages,
	onAcceptProposal,
	onAddBeforeImages,
	onRemoveBeforeImage,
	onAddAfterImages,
	onRemoveAfterImage,
	onRejectProposal,
}: {
	project: Project;
	proposals: ProjectProposalItem[];
	proposalsLoading: boolean;
	proposalActionError: ProposalActionError | null;
	proposalActionLoadingId: string | null;
	userType?: string | null;
	isSavingProjectImages: boolean;
	onAcceptProposal: (proposalId: string) => void;
	onAddBeforeImages: (urls: string[]) => void;
	onRemoveBeforeImage: (index: number) => void;
	onAddAfterImages: (urls: string[]) => void;
	onRemoveAfterImage: (index: number) => void;
	onRejectProposal: (proposalId: string) => void;
}): React.ReactElement {
	const { t } = useTranslation();
	const canManageTransformationImages = userType === "homeowner";

	return (
		<div className="space-y-6">
			{/* Project Details */}
			<div>
				<p
					style={{
						fontFamily: "var(--font-display)",
						fontStyle: "italic",
						fontSize: 18,
						fontWeight: 500,
						color: "var(--color-dashboard-text)",
						marginBottom: 20,
						letterSpacing: "-0.01em",
					}}
				>
					{t("project.projectDetails")}
				</p>
				<dl className="grid gap-4 sm:grid-cols-2">
					<div>
						<dt
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 8.5,
								letterSpacing: "0.12em",
								color: "var(--color-dashboard-muted)",
								textTransform: "uppercase",
							}}
						>
							{t("project.scope")}
						</dt>
						<dd
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-text)",
								marginTop: 4,
							}}
						>
							{project.scope === "full_home"
								? t("common.fullHome")
								: t("common.partial")}
						</dd>
					</div>
					{project.scopeDetails?.rooms &&
						project.scopeDetails.rooms.length > 0 && (
							<div>
								<dt
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 8.5,
										letterSpacing: "0.12em",
										color: "var(--color-dashboard-muted)",
										textTransform: "uppercase",
									}}
								>
									{t("project.rooms")}
								</dt>
								<dd
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 14,
										color: "var(--color-dashboard-text)",
										marginTop: 4,
									}}
								>
									{project.scopeDetails.rooms.join(", ")}
								</dd>
							</div>
						)}
					<div>
						<dt
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 8.5,
								letterSpacing: "0.12em",
								color: "var(--color-dashboard-muted)",
								textTransform: "uppercase",
							}}
						>
							{t("project.budget")}
						</dt>
						<dd
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontSize: 15,
								color: "var(--color-dashboard-text)",
								marginTop: 4,
							}}
						>
							{project.budgetMin != null && project.budgetMax != null
								? `${formatPrice(project.budgetMin)} - ${formatPrice(project.budgetMax)}`
								: t("project.notSet")}
						</dd>
					</div>
					<div>
						<dt
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 8.5,
								letterSpacing: "0.12em",
								color: "var(--color-dashboard-muted)",
								textTransform: "uppercase",
							}}
						>
							{t("project.timeline")}
						</dt>
						<dd
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-text)",
								marginTop: 4,
							}}
						>
							{project.timelineWeeks
								? `${project.timelineWeeks} ${t("common.weeks")}`
								: t("project.notSet")}
						</dd>
					</div>
					<div>
						<dt
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 8.5,
								letterSpacing: "0.12em",
								color: "var(--color-dashboard-muted)",
								textTransform: "uppercase",
							}}
						>
							{t("project.startDate")}
						</dt>
						<dd
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-text)",
								marginTop: 4,
							}}
						>
							{project.startTimeline ?? t("project.notSpecified")}
						</dd>
					</div>
					<div>
						<dt
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 8.5,
								letterSpacing: "0.12em",
								color: "var(--color-dashboard-muted)",
								textTransform: "uppercase",
							}}
						>
							{t("project.created")}
						</dt>
						<dd
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-text)",
								marginTop: 4,
							}}
						>
							{formatDate(project.createdAt)}
						</dd>
					</div>
					{project.scopeDetails?.notes && (
						<div className="sm:col-span-2">
							<dt
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 8.5,
									letterSpacing: "0.12em",
									color: "var(--color-dashboard-muted)",
									textTransform: "uppercase",
								}}
							>
								{t("project.notes")}
							</dt>
							<dd
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-muted-alt)",
									marginTop: 4,
									lineHeight: 1.55,
								}}
							>
								{project.scopeDetails.notes}
							</dd>
						</div>
					)}
				</dl>
			</div>

			{canManageTransformationImages && (
				<div
					style={{
						background: "var(--color-dashboard-warm-450)",
						borderRadius: 11,
						border: "1px solid var(--color-dashboard-border)",
						padding: 24,
					}}
				>
					<p
						style={{
							fontFamily: "var(--font-display)",
							fontStyle: "italic",
							fontSize: 18,
							fontWeight: 500,
							color: "var(--color-dashboard-text)",
							marginBottom: 4,
						}}
					>
						Before / After Images
					</p>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 12.5,
							color: "var(--color-dashboard-muted)",
							marginBottom: 20,
						}}
					>
						Upload before and after images in separate columns.
					</p>
					<div className="grid gap-4 sm:grid-cols-2">
						<MultiImageUpload
							label="Before images"
							currentUrls={project.beforeImages || []}
							maxImages={24}
							onUpload={onAddBeforeImages}
							onRemove={onRemoveBeforeImage}
						/>
						<MultiImageUpload
							label="After images"
							currentUrls={project.afterImages || []}
							maxImages={24}
							onUpload={onAddAfterImages}
							onRemove={onRemoveAfterImage}
						/>
						{isSavingProjectImages && (
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 11,
									color: "var(--color-dashboard-muted)",
								}}
								className="sm:col-span-2"
							>
								Saving transformation images...
							</p>
						)}
					</div>
				</div>
			)}

			{/* Assigned Designer */}
			{project.designerId && (
				<div
					style={{
						background: "var(--color-dashboard-warm-450)",
						borderRadius: 11,
						border: "1px solid var(--color-dashboard-border)",
						padding: 24,
					}}
				>
					<div className="flex items-center gap-3 mb-3">
						<div
							style={{
								width: 36,
								height: 36,
								borderRadius: 7,
								background: "var(--color-dashboard-warm-200)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								color: "var(--color-dashboard-accent-dark)",
							}}
						>
							<User style={{ width: 16, height: 16 }} />
						</div>
						<p
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontSize: 16,
								fontWeight: 500,
								color: "var(--color-dashboard-text)",
							}}
						>
							{t("project.assignedDesigner")}
						</p>
					</div>
					<p
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 9,
							letterSpacing: "0.1em",
							color: "var(--color-dashboard-muted)",
							textTransform: "uppercase",
							marginLeft: 49,
						}}
					>
						{t("project.designerIdLabel", { id: project.designerId })}
					</p>
				</div>
			)}

			{/* Proposals */}
			{userType === "homeowner" && (
				<div
					style={{
						background: "var(--color-dashboard-warm-450)",
						borderRadius: 11,
						border: "1px solid var(--color-dashboard-border)",
						padding: 24,
					}}
				>
					<p
						style={{
							fontFamily: "var(--font-display)",
							fontStyle: "italic",
							fontSize: 18,
							fontWeight: 500,
							color: "var(--color-dashboard-text)",
							marginBottom: 4,
						}}
					>
						{t("project.proposals")}
					</p>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 12.5,
							color: "var(--color-dashboard-muted)",
							marginBottom: 20,
						}}
					>
						{t("project.designerProposals")}
					</p>
					{proposalsLoading ? (
						<div className="space-y-3">
							<Skeleton className="h-20 w-full" />
							<Skeleton className="h-20 w-full" />
						</div>
					) : proposals.length === 0 ? (
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 13.5,
								color: "var(--color-dashboard-muted)",
								fontStyle: "italic",
							}}
						>
							{t("project.noProposalsYet")}
						</p>
					) : (
						<div className="space-y-4">
							{proposals.map((item) => (
								<div
									key={item.request.id}
									style={{
										background: "var(--color-dashboard-warm-150)",
										border: "1px solid var(--color-dashboard-divider)",
										borderRadius: 10,
										padding: 20,
									}}
								>
									<div className="flex items-start justify-between">
										<div>
											<p
												style={{
													fontFamily: "var(--font-display)",
													fontStyle: "italic",
													fontSize: 16,
													fontWeight: 500,
													color: "var(--color-dashboard-text)",
												}}
											>
												{item.designer.name}
											</p>
											{item.designer.firmName && (
												<p
													style={{
														fontFamily: "var(--font-mono)",
														fontSize: 9,
														letterSpacing: "0.1em",
														color: "var(--color-dashboard-muted)",
														textTransform: "uppercase",
														marginTop: 4,
													}}
												>
													{item.designer.firmName}
												</p>
											)}
										</div>
										<span
											style={{
												borderRadius: 14,
												padding: "4px 12px",
												fontSize: 10,
												fontWeight: 600,
												fontFamily: "var(--font-mono)",
												letterSpacing: "0.08em",
												textTransform: "uppercase",
												background:
													item.request.status === "accepted"
														? "var(--color-dashboard-pill-done-bg)"
														: item.request.status === "rejected"
															? "var(--color-dashboard-pill-risk-bg)"
															: item.request.status === "proposal_submitted"
																? "var(--color-dashboard-pill-info-bg)"
																: "var(--color-dashboard-warm-200)",
												color:
													item.request.status === "accepted"
														? "var(--color-dashboard-pill-done)"
														: item.request.status === "rejected"
															? "var(--color-dashboard-pill-risk)"
															: item.request.status === "proposal_submitted"
																? "var(--color-dashboard-pill-info)"
																: "var(--color-dashboard-muted)",
											}}
										>
											{item.request.status.replace(/_/g, " ")}
										</span>
									</div>
									{item.proposal && (
										<div className="mt-4 flex gap-8">
											<div>
												<span
													style={{
														fontFamily: "var(--font-mono)",
														fontSize: 8.5,
														letterSpacing: "0.1em",
														color: "var(--color-dashboard-muted)",
														textTransform: "uppercase",
													}}
												>
													{t("project.estimate")}{" "}
												</span>
												<span
													style={{
														fontFamily: "var(--font-display)",
														fontWeight: 500,
														fontSize: 15,
														color: "var(--color-dashboard-text)",
													}}
												>
													{formatPrice(item.proposal.costEstimate)}
												</span>
											</div>
											<div>
												<span
													style={{
														fontFamily: "var(--font-mono)",
														fontSize: 8.5,
														letterSpacing: "0.1em",
														color: "var(--color-dashboard-muted)",
														textTransform: "uppercase",
													}}
												>
													{t("project.timeline")}:{" "}
												</span>
												<span
													style={{
														fontFamily: "var(--font-display)",
														fontWeight: 500,
														fontSize: 15,
														color: "var(--color-dashboard-text)",
													}}
												>
													{item.proposal.timelineWeeks} {t("common.weeks")}
												</span>
											</div>
										</div>
									)}
									{userType === "homeowner" &&
										item.request.status === "proposal_submitted" &&
										item.proposal && (
											<div
												style={{
													marginTop: 16,
													paddingTop: 16,
													borderTop: "1px solid var(--color-dashboard-divider)",
												}}
											>
												{proposalActionError &&
													proposalActionError.proposalId ===
														item.proposal.id && (
														<p
															style={{
																marginBottom: 12,
																fontSize: 13,
																color: "var(--color-dashboard-pill-risk)",
															}}
														>
															{proposalActionError.message}
														</p>
													)}
												<div className="flex gap-3">
													<Button
														size="sm"
														isLoading={
															proposalActionLoadingId === item.proposal.id
														}
														disabled={!!proposalActionLoadingId}
														onClick={() =>
															item.proposal?.id &&
															onAcceptProposal(item.proposal.id)
														}
													>
														<CheckCircle2 className="h-4 w-4" />
														{t("proposals.accept")}
													</Button>
													<Button
														variant="destructive"
														size="sm"
														isLoading={
															proposalActionLoadingId === item.proposal.id
														}
														disabled={!!proposalActionLoadingId}
														onClick={() =>
															item.proposal?.id &&
															onRejectProposal(item.proposal.id)
														}
													>
														<XCircle className="h-4 w-4" />
														{t("proposals.reject")}
													</Button>
												</div>
											</div>
										)}
								</div>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
}

// ============================================================================
// TASKS TAB
// ============================================================================

function TasksTab({
	projectId,
	tasks,
	milestones,
	onRefresh,
	userType,
	userId,
}: {
	projectId: string;
	tasks: Task[];
	milestones: Milestone[];
	onRefresh: () => void;
	userType?: string | null;
	userId?: string | null;
}): React.ReactElement {
	const { t } = useTranslation();
	const [showForm, setShowForm] = useState(false);
	const [newTitle, setNewTitle] = useState("");
	const [newDescription, setNewDescription] = useState("");
	// "" is "No milestone", the select's first option.
	const [newMilestoneId, setNewMilestoneId] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const doneN = tasks.filter((task) => task.status === "completed").length;

	const handleCreateTask = async () => {
		if (!newTitle.trim()) return;
		setIsSubmitting(true);
		try {
			const result = await createTask(projectId, {
				title: newTitle.trim(),
				description: newDescription.trim() || undefined,
				milestoneId: newMilestoneId || undefined,
			});
			if (result.success) {
				setNewTitle("");
				setNewDescription("");
				setNewMilestoneId("");
				setShowForm(false);
				onRefresh();
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleStatusChange = async (taskId: string, status: Task["status"]) => {
		try {
			const result = await updateTaskStatus(taskId, status);
			if (result.success) {
				onRefresh();
			} else {
				toast.error(
					t("project.failedToUpdateStatus", "Failed to update task status"),
				);
			}
		} catch {
			toast.error(
				t("project.failedToUpdateStatus", "Failed to update task status"),
			);
		}
	};

	const handleDelete = async (taskId: string) => {
		try {
			const result = await deleteTask(taskId);
			if (result.success) {
				onRefresh();
			} else {
				toast.error(t("project.failedToDeleteTask", "Failed to delete task"));
			}
		} catch {
			toast.error(t("project.failedToDeleteTask", "Failed to delete task"));
		}
	};

	const groupedTasks = groupTasksByMilestone(tasks, milestones);

	const renderTaskRow = (task: Task) => {
		const isCompleted = task.status === "completed";
		const isProgress = task.status === "in_progress";

		const assigneeName = task.assigneeDisplayName;
		const roleBy = task.assigneeProfileType
			? task.assigneeProfileType.charAt(0).toUpperCase() +
				task.assigneeProfileType.slice(1)
			: "Unassigned";
		const initials =
			assigneeName
				.split(" ")
				.map((n) => n[0])
				.join("")
				.substring(0, 2)
				.toUpperCase() || "?";
		const tone = task.assigneeProfileType === "contractor" ? "stone" : "ink";
		const canUpdateStatus =
			userType !== "contractor" || task.assignedTo === userId;

		return (
			<FloatPanel key={task.id} elevation={1} padding={0}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 16,
						padding: "16px 22px",
					}}
				>
					<button
						type="button"
						onClick={() => {
							if (canUpdateStatus) {
								handleStatusChange(task.id, isCompleted ? "todo" : "completed");
							}
						}}
						disabled={!canUpdateStatus}
						style={{
							width: 24,
							height: 24,
							borderRadius: 5,
							border: 0,
							cursor: canUpdateStatus ? "pointer" : "not-allowed",
							flexShrink: 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							background: isCompleted
								? "var(--color-dashboard-accent)"
								: "var(--color-dashboard-bg)",
							boxShadow: isCompleted
								? "none"
								: "inset 0 0 0 1.5px var(--color-dashboard-progress-empty)",
							transition: "all 180ms ease",
							opacity: canUpdateStatus ? 1 : 0.6,
						}}
					>
						{isCompleted && <Check className="h-4 w-4 text-dashboard-text" />}
					</button>
					<div style={{ flex: 1, minWidth: 0 }}>
						<div
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14.5,
								fontWeight: 600,
								color: isCompleted
									? "var(--color-dashboard-muted-light)"
									: "var(--color-dashboard-text)",
								textDecoration: isCompleted ? "line-through" : "none",
							}}
						>
							{task.title}
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								marginTop: 6,
							}}
						>
							<Avatar initials={initials} tone={tone} size={20} />
							<span
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 12,
									color: "var(--color-dashboard-muted)",
								}}
							>
								{assigneeName}
							</span>
							<span style={{ color: "var(--color-dashboard-progress-empty)" }}>
								·
							</span>
							<Pill
								tone="neutral"
								dot={false}
								style={{ height: 20, fontSize: 10 }}
							>
								{roleBy}
							</Pill>
							{isProgress && (
								<>
									<span
										style={{ color: "var(--color-dashboard-progress-empty)" }}
									>
										·
									</span>
									<span className="text-xs text-dashboard-pill-info font-semibold">
										In Progress
									</span>
								</>
							)}
						</div>
						{task.description && (
							<p
								style={{
									marginTop: 6,
									fontSize: 12.5,
									color: "var(--color-dashboard-muted-alt)",
								}}
							>
								{task.description}
							</p>
						)}
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
						{task.status !== "completed" && canUpdateStatus && (
							<button
								type="button"
								onClick={() =>
									handleStatusChange(
										task.id,
										isProgress ? "todo" : "in_progress",
									)
								}
								className="text-xs font-semibold text-[var(--color-dashboard-accent)] hover:underline"
							>
								{isProgress ? "Mark To Do" : "Start"}
							</button>
						)}
						{userType !== "contractor" && (
							<button
								type="button"
								onClick={() => handleDelete(task.id)}
								style={{
									background: "transparent",
									border: 0,
									cursor: "pointer",
									color: "var(--color-destructive)",
									display: "flex",
									alignItems: "center",
								}}
							>
								<Trash2 className="h-4 w-4" />
							</button>
						)}
					</div>
				</div>
			</FloatPanel>
		);
	};

	return (
		<div className="space-y-6">
			<div className="flex items-end justify-between">
				<div>
					<Eyebrow style={{ fontSize: 9 }}>
						TASKS · {doneN}/{tasks.length} DONE
					</Eyebrow>
					<h2
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 34,
							lineHeight: 1.05,
							letterSpacing: "-0.02em",
							color: "var(--color-dashboard-text)",
							margin: "8px 0 0",
						}}
					>
						What's{" "}
						<ItalicAccent
							style={{
								fontSize: 34,
								color: "var(--color-dashboard-accent-display)",
							}}
						>
							moving
						</ItalicAccent>
						.
					</h2>
				</div>
				{userType !== "contractor" && (
					<StudioButton
						variant="primary"
						size="md"
						icon={Plus}
						onClick={() => setShowForm(!showForm)}
					>
						New task
					</StudioButton>
				)}
			</div>

			<FloatPanel elevation={1} padding={20}>
				<div
					style={{
						display: "flex",
						marginBottom: 8,
						justifyContent: "space-between",
					}}
				>
					<Eyebrow style={{ fontSize: 9 }}>PROGRESS</Eyebrow>
					<span
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 13,
							color: "var(--color-dashboard-muted-alt)",
						}}
					>
						{tasks.length > 0 ? Math.round((doneN / tasks.length) * 100) : 0}%
						complete
					</span>
				</div>
				<Progress
					value={tasks.length > 0 ? (doneN / tasks.length) * 100 : 0}
					fillColor="var(--color-dashboard-accent)"
					height={6}
				/>
			</FloatPanel>

			{showForm && (
				<FloatPanel elevation={2} padding={22}>
					<Eyebrow style={{ fontSize: 9, marginBottom: 14 }}>NEW TASK</Eyebrow>
					<div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
						<FieldInput
							label="Task Title"
							value={newTitle}
							onChange={(e) => setNewTitle(e.target.value)}
							placeholder="What needs doing"
						/>
						<FieldInput
							label="Description (Optional)"
							value={newDescription}
							onChange={(e) => setNewDescription(e.target.value)}
							placeholder="Provide details about the task"
						/>
						{milestones.length > 0 && (
							<FieldSelect
								label="Milestone"
								value={newMilestoneId}
								onChange={(e) => setNewMilestoneId(e.target.value)}
								options={[
									{ value: "", label: "No milestone" },
									...milestones.map((m) => ({ value: m.id, label: m.title })),
								]}
							/>
						)}
						<div
							style={{
								display: "flex",
								gap: 12,
								justifyContent: "flex-end",
								marginTop: 6,
							}}
						>
							<StudioButton
								variant="primary"
								size="md"
								onClick={handleCreateTask}
								disabled={isSubmitting || !newTitle.trim()}
							>
								{isSubmitting ? "Adding..." : "Add Task"}
							</StudioButton>
							<StudioButton
								variant="secondary"
								size="md"
								onClick={() => {
									setShowForm(false);
									setNewTitle("");
									setNewDescription("");
									setNewMilestoneId("");
								}}
							>
								Cancel
							</StudioButton>
						</div>
					</div>
				</FloatPanel>
			)}

			<div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
				{milestones.length === 0 ? (
					<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
						{tasks.length === 0 ? (
							<p className="text-sm text-muted-foreground text-center py-6">
								{t("project.noTasksToDo")}
							</p>
						) : (
							tasks.map((task) => renderTaskRow(task))
						)}
					</div>
				) : (
					<>
						{milestones.map((m) => {
							const milestoneTasks = groupedTasks.byMilestone.get(m.id) ?? [];
							return (
								<section key={m.id} aria-label={m.title} className="space-y-3">
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "baseline",
											borderBottom: "1px solid var(--color-dashboard-border)",
											paddingBottom: 6,
										}}
									>
										<span
											style={{
												fontFamily: "var(--font-display)",
												fontStyle: "italic",
												fontSize: 18,
												fontWeight: 500,
												color: "var(--color-dashboard-text)",
											}}
										>
											{m.title}
										</span>
										<span
											style={{
												fontFamily: "var(--font-mono)",
												fontSize: 10,
												color: "var(--color-dashboard-muted)",
											}}
										>
											{
												milestoneTasks.filter((t) => t.status === "completed")
													.length
											}
											/{milestoneTasks.length} DONE
										</span>
									</div>
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											gap: 10,
										}}
									>
										{milestoneTasks.length === 0 ? (
											<p className="text-xs text-muted-foreground italic">
												No tasks under this milestone.
											</p>
										) : (
											milestoneTasks.map((task) => renderTaskRow(task))
										)}
									</div>
								</section>
							);
						})}
						{/* Tasks in no milestone */}
						{groupedTasks.general.length > 0 && (
							<section aria-label={GENERAL_TASKS_LABEL} className="space-y-3">
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "baseline",
										borderBottom: "1px solid var(--color-dashboard-border)",
										paddingBottom: 6,
									}}
								>
									<span
										style={{
											fontFamily: "var(--font-display)",
											fontStyle: "italic",
											fontSize: 18,
											fontWeight: 500,
											color: "var(--color-dashboard-text)",
										}}
									>
										{GENERAL_TASKS_LABEL}
									</span>
									<span
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 10,
											color: "var(--color-dashboard-muted)",
										}}
									>
										{
											groupedTasks.general.filter(
												(t) => t.status === "completed",
											).length
										}
										/{groupedTasks.general.length} DONE
									</span>
								</div>
								<div
									style={{ display: "flex", flexDirection: "column", gap: 10 }}
								>
									{groupedTasks.general.map((task) => renderTaskRow(task))}
								</div>
							</section>
						)}
					</>
				)}
			</div>
		</div>
	);
}

// ============================================================================
// MILESTONES TAB
// ============================================================================

function MilestonesTab({
	projectId,
	milestones,
	tasks = [],
	onRefresh,
	onRefreshTasks,
	releaseErrors,
	userType,
	userId,
	onReleasePayment,
	releaseLoading,
}: {
	projectId: string;
	milestones: Milestone[];
	tasks?: Task[];
	onRefresh: () => void;
	onRefreshTasks?: () => void;
	releaseErrors?: Record<string, string>;
	userType?: string | null;
	userId?: string | null;
	onReleasePayment?: (milestoneId: string) => void;
	releaseLoading?: string | null;
}): React.ReactElement {
	const canToggleTask = (task: Task) => {
		if (userType === "contractor") {
			return task.assignedTo === userId;
		}
		return true;
	};
	const { t } = useTranslation();
	const [showForm, setShowForm] = useState(false);
	const [newTitle, setNewTitle] = useState("");
	const [newAmount, setNewAmount] = useState("");
	const [newDueDate, setNewDueDate] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editTitle, setEditTitle] = useState("");
	const [editAmount, setEditAmount] = useState("");
	const [editDate, setEditDate] = useState("");
	const [editSaving, setEditSaving] = useState(false);

	// Nested tasks inline adding state
	const [taskFor, setTaskFor] = useState<string | null>(null);
	const [taskText, setTaskText] = useState("");

	const totalAmount = milestones.reduce(
		(sum, m) => sum + (m.paymentAmount ?? 0),
		0,
	);
	const paidMilestones = milestones.filter((m) => m.paymentStatus === "paid");
	const totalPaid = paidMilestones.reduce(
		(sum, m) => sum + (m.paymentAmount ?? 0),
		0,
	);

	const handleCreateMilestone = async () => {
		if (!newTitle.trim() || !newAmount) return;
		setIsSubmitting(true);
		try {
			const result = await createMilestone(projectId, {
				title: newTitle.trim(),
				paymentAmount: parseFloat(newAmount),
				targetDate: newDueDate || undefined,
			});
			if (result.success) {
				setNewTitle("");
				setNewAmount("");
				setNewDueDate("");
				setShowForm(false);
				onRefresh();
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleMarkPaid = async (milestoneId: string) => {
		const result = await markMilestonePaid(milestoneId);
		if (result.success) {
			onRefresh();
		}
	};

	const handleAddTask = async (milestoneId: string) => {
		if (!taskText.trim()) return;
		try {
			const result = await createTask(projectId, {
				title: taskText.trim(),
				milestoneId,
			});
			if (result.success) {
				setTaskText("");
				setTaskFor(null);
				onRefreshTasks?.();
				toast.success("Task added to milestone");
			} else {
				toast.error(result.error || "Failed to create task");
			}
		} catch {
			toast.error("Failed to create task");
		}
	};

	const handleToggleTaskStatus = async (
		taskId: string,
		currentStatus: Task["status"],
	) => {
		try {
			const nextStatus = currentStatus === "completed" ? "todo" : "completed";
			const result = await updateTaskStatus(taskId, nextStatus);
			if (result.success) {
				onRefreshTasks?.();
			}
		} catch {
			toast.error("Failed to toggle task status");
		}
	};

	const groupedTasks = groupTasksByMilestone(tasks, milestones);

	const APPROVAL: Record<string, { tone: PillTone; label: string }> = {
		confirmed: { tone: "done", label: "Homeowner confirmed" },
		approved: { tone: "active", label: "Homeowner approved" },
		pending: { tone: "awaiting", label: "Awaiting homeowner approval" },
		locked: { tone: "neutral", label: "Locked — earlier work pending" },
	};

	const handleToggleStatus = async (milestone: Milestone) => {
		const newStatus =
			milestone.status === "completed" ? "pending" : "completed";
		const result = await updateMilestoneStatus(milestone.id, newStatus);
		if (result.success) {
			onRefresh();
		} else {
			toast.error(result.error || t("common.somethingWentWrong"));
		}
	};

	const startEditMilestone = (milestone: Milestone) => {
		setEditingId(milestone.id);
		setEditTitle(milestone.title);
		setEditAmount(
			milestone.paymentAmount != null ? String(milestone.paymentAmount) : "",
		);
		setEditDate(milestone.targetDate ? milestone.targetDate.split("T")[0] : "");
	};

	const handleSaveEdit = async () => {
		if (!editingId || !editTitle.trim()) return;
		setEditSaving(true);
		try {
			const result = await updateMilestone(editingId, {
				title: editTitle.trim(),
				paymentAmount: editAmount ? parseFloat(editAmount) : undefined,
				targetDate: editDate || undefined,
			});
			if (result.success) {
				setEditingId(null);
				onRefresh();
				toast.success(t("common.saveChanges"));
			} else {
				toast.error(result.error || t("common.somethingWentWrong"));
			}
		} finally {
			setEditSaving(false);
		}
	};

	const handleDeleteMilestone = async (milestoneId: string) => {
		const result = await deleteMilestone(milestoneId);
		if (result.success) {
			onRefresh();
		} else {
			toast.error(result.error || t("common.somethingWentWrong"));
		}
	};

	return (
		<div className="space-y-6">
			<div className="flex items-end justify-between flex-wrap gap-4">
				<div>
					<Eyebrow style={{ fontSize: 9 }}>
						MILESTONES · {milestones.length} · {formatPrice(totalAmount)}{" "}
						SCHEDULED
					</Eyebrow>
					<h2
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 34,
							lineHeight: 1.05,
							letterSpacing: "-0.02em",
							color: "var(--color-dashboard-text)",
							margin: "8px 0 0",
						}}
					>
						Chapters to{" "}
						<ItalicAccent
							style={{
								fontSize: 34,
								color: "var(--color-dashboard-accent-display)",
							}}
						>
							handover
						</ItalicAccent>
						.
					</h2>
				</div>
				{userType !== "contractor" && (
					<StudioButton
						variant="primary"
						size="md"
						icon={Plus}
						onClick={() => {
							setShowForm(!showForm);
							setEditingId(null);
						}}
					>
						Add milestone
					</StudioButton>
				)}
			</div>

			<div
				style={{
					display: "flex",
					gap: 10,
					padding: "14px 18px",
					background: "var(--color-dashboard-warm-050)",
					borderRadius: 8,
					alignItems: "flex-start",
				}}
			>
				<Info
					size={15}
					className="text-[var(--color-dashboard-accent)] mt-1 shrink-0"
				/>
				<span
					style={{
						fontFamily: "var(--font-body)",
						fontSize: 12.5,
						lineHeight: 1.5,
						color: "var(--color-dashboard-muted-alt)",
					}}
				>
					Each milestone carries its own tasks and escrow payment. Payment
					releases only after you mark a milestone complete <em>and</em> the
					homeowner confirms.
				</span>
			</div>

			{/* Summary totals */}
			<div className="grid grid-cols-2 gap-4 sm:grid-cols-2">
				<FloatPanel elevation={2} padding={22}>
					<Eyebrow style={{ fontSize: 9 }}>TOTAL PAID</Eyebrow>
					<div
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 32,
							color: "var(--color-dashboard-pill-done)",
							letterSpacing: "-0.02em",
							marginTop: 8,
							fontVariantNumeric: "tabular-nums",
						}}
					>
						{formatPrice(totalPaid)}
					</div>
					{totalAmount > 0 && (
						<div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-dashboard-border">
							<div
								className="h-full rounded-full bg-dashboard-success-fill transition-all"
								style={{
									width: `${Math.min(100, (totalPaid / totalAmount) * 100)}%`,
								}}
							/>
						</div>
					)}
				</FloatPanel>
				<FloatPanel elevation={2} padding={22}>
					<Eyebrow style={{ fontSize: 9 }}>TOTAL SCHEDULED VALUE</Eyebrow>
					<div
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 32,
							color: "var(--color-dashboard-ink-soft)",
							letterSpacing: "-0.02em",
							marginTop: 8,
							fontVariantNumeric: "tabular-nums",
						}}
					>
						{formatPrice(totalAmount)}
					</div>
				</FloatPanel>
			</div>

			{showForm && (
				<FloatPanel
					elevation={2}
					padding={22}
					style={{ borderLeft: "3px solid var(--color-dashboard-accent)" }}
				>
					<Eyebrow style={{ fontSize: 9, marginBottom: 14 }}>
						NEW MILESTONE
					</Eyebrow>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
						<FieldInput
							label="Milestone title"
							value={newTitle}
							onChange={(e) => setNewTitle(e.target.value)}
							placeholder="e.g. Carpentry installation"
						/>
						<FieldInput
							label="Value (Rs)"
							value={newAmount}
							onChange={(e) => setNewAmount(e.target.value)}
							placeholder="e.g. 150000"
							type="number"
						/>
						<div className="flex flex-col">
							<Eyebrow style={{ fontSize: 8, marginBottom: 6 }}>
								Due Date
							</Eyebrow>
							<input
								type="date"
								value={newDueDate}
								onChange={(e) => setNewDueDate(e.target.value)}
								style={{
									width: "100%",
									height: 40,
									padding: "0 12px",
									border: "1px solid var(--color-dashboard-progress-empty)",
									borderRadius: 7,
									background: "var(--color-dashboard-warm-450)",
									fontFamily: "var(--font-body)",
									fontSize: 13.5,
									color: "var(--color-dashboard-text)",
									outline: "none",
								}}
							/>
						</div>
					</div>
					<div className="flex gap-2 justify-end mt-4">
						<StudioButton
							variant="primary"
							size="md"
							onClick={handleCreateMilestone}
							disabled={isSubmitting}
						>
							Create Milestone
						</StudioButton>
						<StudioButton
							variant="secondary"
							size="md"
							onClick={() => {
								setShowForm(false);
								setNewTitle("");
								setNewAmount("");
								setNewDueDate("");
							}}
						>
							Cancel
						</StudioButton>
					</div>
				</FloatPanel>
			)}

			{milestones.length === 0 ? (
				<FloatPanel elevation={1} padding={24}>
					<p className="text-sm text-[var(--color-dashboard-muted)] text-center font-medium">
						No milestones scheduled yet.
					</p>
				</FloatPanel>
			) : (
				<div style={{ position: "relative", paddingLeft: 34 }}>
					<div
						style={{
							position: "absolute",
							left: 13,
							top: 8,
							bottom: 8,
							width: 2,
							background: "var(--color-dashboard-border)",
						}}
					/>
					<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
						{milestones.map((m, idx) => {
							const done = m.status === "completed";
							const active = m.status === "pending";
							const milestoneTasks = groupedTasks.byMilestone.get(m.id) ?? [];

							// derive approval status
							const apKey =
								m.paymentStatus === "paid"
									? "confirmed"
									: done
										? "approved"
										: active
											? "pending"
											: "locked";
							const ap = APPROVAL[apKey];

							if (editingId === m.id) {
								return (
									<div key={m.id}>
										<FloatPanel
											elevation={2}
											padding={22}
											style={{
												borderLeft: "3px solid var(--color-dashboard-accent)",
											}}
										>
											<Eyebrow style={{ fontSize: 9, marginBottom: 14 }}>
												EDIT MILESTONE
											</Eyebrow>
											<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
												<FieldInput
													label="Milestone title"
													value={editTitle}
													onChange={(e) => setEditTitle(e.target.value)}
												/>
												<FieldInput
													label="Value (Rs)"
													value={editAmount}
													onChange={(e) => setEditAmount(e.target.value)}
													type="number"
												/>
												<div className="flex flex-col">
													<Eyebrow style={{ fontSize: 8, marginBottom: 6 }}>
														Due Date
													</Eyebrow>
													<input
														type="date"
														value={editDate}
														onChange={(e) => setEditDate(e.target.value)}
														style={{
															width: "100%",
															height: 40,
															padding: "0 12px",
															border:
																"1px solid var(--color-dashboard-progress-empty)",
															borderRadius: 7,
															background: "var(--color-dashboard-warm-450)",
															fontFamily: "var(--font-body)",
															fontSize: 13.5,
															color: "var(--color-dashboard-text)",
															outline: "none",
														}}
													/>
												</div>
											</div>
											<div className="flex gap-2 justify-end mt-4">
												<StudioButton
													variant="primary"
													size="md"
													onClick={handleSaveEdit}
													disabled={editSaving}
												>
													Save Changes
												</StudioButton>
												<StudioButton
													variant="secondary"
													size="md"
													onClick={() => setEditingId(null)}
												>
													Cancel
												</StudioButton>
											</div>
										</FloatPanel>
									</div>
								);
							}

							return (
								<div key={m.id} style={{ position: "relative" }}>
									<span
										style={{
											position: "absolute",
											left: -34,
											top: 22,
											width: 28,
											height: 28,
											borderRadius: "50%",
											background: done
												? "var(--color-dashboard-success-fill)"
												: active
													? "var(--color-dashboard-accent)"
													: "var(--color-dashboard-warm-300)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											boxShadow: "0 0 0 4px var(--color-dashboard-surface)",
											zIndex: 1,
										}}
									>
										{done ? (
											<Check size={15} className="text-dashboard-on-fill" />
										) : (
											<span
												style={{
													fontFamily: "var(--font-mono)",
													fontSize: 11,
													fontWeight: 700,
													color: active
														? "var(--color-dashboard-bg)"
														: "var(--color-dashboard-muted)",
												}}
											>
												{idx + 1}
											</span>
										)}
									</span>

									<FloatPanel
										elevation={active ? 3 : 1}
										padding={22}
										style={{
											borderLeft: active
												? "3px solid var(--color-dashboard-accent)"
												: "1px solid var(--color-dashboard-border)",
										}}
									>
										<div className="flex justify-between items-start gap-4 flex-wrap">
											<div className="min-w-0 flex-1">
												<div
													style={{
														fontFamily: "var(--font-display)",
														fontStyle: "italic",
														fontSize: 20,
														fontWeight: 500,
														color: "var(--color-dashboard-text)",
													}}
												>
													{m.title}
												</div>
												<div className="flex flex-wrap items-center gap-2 mt-2">
													<Pill
														tone={
															done
																? "done"
																: active
																	? "active"
																	: m.status === "pending"
																		? "awaiting"
																		: "neutral"
														}
													>
														{done
															? "Completed"
															: active
																? "In progress"
																: "Upcoming"}
													</Pill>
													<Pill tone={ap.tone} dot={false}>
														{ap.label}
													</Pill>
													{m.targetDate && (
														<span
															style={{
																fontFamily: "var(--font-mono)",
																fontSize: 9,
																letterSpacing: "0.1em",
																color: "var(--color-dashboard-muted)",
																display: "inline-flex",
																alignItems: "center",
															}}
														>
															<Calendar size={11} style={{ marginRight: 4 }} />
															{formatDate(m.targetDate)}
														</span>
													)}
												</div>
											</div>
											<div className="text-right shrink-0">
												<Eyebrow style={{ fontSize: 8 }}>VALUE</Eyebrow>
												<div
													style={{
														fontFamily: "var(--font-display)",
														fontWeight: 500,
														fontSize: 24,
														color: "var(--color-dashboard-ink-soft)",
														letterSpacing: "-0.015em",
														marginTop: 4,
														fontVariantNumeric: "tabular-nums",
													}}
												>
													{formatPrice(m.paymentAmount ?? 0)}
												</div>
											</div>
										</div>

										{/* NESTED PAYMENTS SUMMARY */}
										{m.paymentStatus === "paid" && m.paidAt && (
											<div className="mt-4 pt-4 border-t border-dashboard-divider">
												<Eyebrow style={{ fontSize: 8, marginBottom: 8 }}>
													ESCROW PAYMENTS
												</Eyebrow>
												<div
													className="flex items-center gap-3 p-3 rounded-lg border border-dashboard-divider"
													style={{
														background: "var(--color-dashboard-warm-150)",
													}}
												>
													<span className="w-2 h-2 rounded-full bg-dashboard-success-fill" />
													<div className="flex-1 min-w-0">
														<div className="font-semibold text-xs text-dashboard-text">
															Escrow Release Credit
														</div>
														<div className="font-mono text-[9px] text-[var(--color-dashboard-muted)] mt-0.5">
															RELEASED ON · {formatDate(m.paidAt)}
														</div>
													</div>
													<div className="text-right">
														<div className="font-bold text-sm text-dashboard-text">
															{formatPrice(m.paymentAmount ?? 0)}
														</div>
														<Pill
															tone="done"
															dot={false}
															style={{ height: 18, fontSize: 9, marginTop: 3 }}
														>
															Released
														</Pill>
													</div>
												</div>
											</div>
										)}

										{/* NESTED TASKS */}
										<section
											aria-label={`Tasks for ${m.title}`}
											className="mt-4 pt-4 border-t border-dashboard-divider"
										>
											<div className="flex justify-between items-center mb-2">
												<Eyebrow style={{ fontSize: 8 }}>
													TASKS ·{" "}
													{
														milestoneTasks.filter(
															(t) => t.status === "completed",
														).length
													}
													/{milestoneTasks.length} DONE
												</Eyebrow>
												{userType !== "contractor" && (
													<button
														type="button"
														onClick={() => {
															setTaskFor(taskFor === m.id ? null : m.id);
															setTaskText("");
														}}
														style={{
															display: "flex",
															alignItems: "center",
															gap: 5,
															border: 0,
															background: "transparent",
															cursor: "pointer",
															fontFamily: "var(--font-body)",
															fontSize: 11.5,
															fontWeight: 600,
															color: "var(--color-dashboard-accent-dark)",
														}}
													>
														<Plus size={13} /> Add task
													</button>
												)}
											</div>

											{milestoneTasks.length === 0 && taskFor !== m.id && (
												<p className="text-xs text-dashboard-muted-light italic">
													No tasks listed under this milestone.
												</p>
											)}

											<div className="flex flex-col gap-2">
												{milestoneTasks.map((t) => {
													const tc = t.status === "completed";
													const aName = t.assigneeDisplayName;
													const rBy = t.assigneeProfileType
														? t.assigneeProfileType.charAt(0).toUpperCase() +
															t.assigneeProfileType.slice(1)
														: "Unassigned";
													const ini =
														aName
															.split(" ")
															.map((n) => n[0])
															.join("")
															.substring(0, 2)
															.toUpperCase() || "?";
													const tTone =
														t.assigneeProfileType === "contractor"
															? "stone"
															: "ink";
													return (
														<div
															key={t.id}
															style={{
																display: "flex",
																alignItems: "center",
																gap: 12,
																padding: "8px 10px",
																borderRadius: 6,
																background: "var(--color-dashboard-warm-150)",
																border:
																	"1px solid var(--color-dashboard-divider)",
															}}
														>
															<button
																type="button"
																onClick={() => {
																	if (canToggleTask(t)) {
																		handleToggleTaskStatus(t.id, t.status);
																	}
																}}
																disabled={!canToggleTask(t)}
																style={{
																	width: 20,
																	height: 20,
																	borderRadius: 4,
																	border: 0,
																	cursor: canToggleTask(t)
																		? "pointer"
																		: "not-allowed",
																	flexShrink: 0,
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	background: tc
																		? "var(--color-dashboard-accent)"
																		: "var(--color-dashboard-bg)",
																	boxShadow: tc
																		? "none"
																		: "inset 0 0 0 1.5px var(--color-dashboard-progress-empty)",
																	opacity: canToggleTask(t) ? 1 : 0.6,
																}}
															>
																{tc && (
																	<Check
																		size={13}
																		className="text-dashboard-on-fill"
																	/>
																)}
															</button>
															<div className="flex-1 min-w-0">
																<div
																	style={{
																		fontFamily: "var(--font-body)",
																		fontSize: 13,
																		fontWeight: 600,
																		color: tc
																			? "var(--color-dashboard-muted-light)"
																			: "var(--color-dashboard-text)",
																		textDecoration: tc
																			? "line-through"
																			: "none",
																	}}
																>
																	{t.title}
																</div>
																<div className="flex items-center gap-2 mt-1">
																	<Avatar
																		initials={ini}
																		tone={tTone}
																		size={16}
																	/>
																	<span
																		style={{
																			fontFamily: "var(--font-body)",
																			fontSize: 11,
																			color: "var(--color-dashboard-muted)",
																		}}
																	>
																		{aName}
																	</span>
																	<Pill
																		tone="neutral"
																		dot={false}
																		style={{ height: 16, fontSize: 8.5 }}
																	>
																		{rBy}
																	</Pill>
																</div>
															</div>
														</div>
													);
												})}
											</div>

											{taskFor === m.id && (
												<div className="flex gap-2 mt-2">
													<input
														type="text"
														value={taskText}
														onChange={(e) => setTaskText(e.target.value)}
														onKeyDown={(e) =>
															e.key === "Enter" && handleAddTask(m.id)
														}
														placeholder="New task title under this milestone..."
														style={{
															flex: 1,
															height: 36,
															padding: "0 12px",
															border:
																"1px solid var(--color-dashboard-progress-empty)",
															borderRadius: 6,
															background: "var(--color-dashboard-warm-450)",
															fontFamily: "var(--font-body)",
															fontSize: 13,
															color: "var(--color-dashboard-text)",
															outline: "none",
														}}
													/>
													<StudioButton
														variant="primary"
														size="sm"
														onClick={() => handleAddTask(m.id)}
													>
														Add
													</StudioButton>
													<StudioButton
														variant="secondary"
														size="sm"
														onClick={() => {
															setTaskFor(null);
															setTaskText("");
														}}
													>
														Cancel
													</StudioButton>
												</div>
											)}
										</section>

										{/* MILESTONE ROW ACTIONS */}
										{userType !== "contractor" && (
											<div className="flex justify-between items-center gap-2 mt-4 pt-4 border-t border-dashboard-divider flex-wrap">
												<div className="flex gap-2">
													{!done ? (
														<StudioButton
															variant="primary"
															size="sm"
															icon={Check}
															onClick={() => handleToggleStatus(m)}
														>
															Mark complete
														</StudioButton>
													) : (
														<StudioButton
															variant="secondary"
															size="sm"
															onClick={() => handleToggleStatus(m)}
														>
															Mark Pending
														</StudioButton>
													)}
													<StudioButton
														variant="secondary"
														size="sm"
														icon={Pencil}
														onClick={() => startEditMilestone(m)}
													>
														Edit
													</StudioButton>
													<AlertDialog>
														<AlertDialogTrigger asChild>
															<Button
																variant="ghost"
																size="sm"
																title={t("project.deleteMilestone")}
																className="text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10"
															>
																<Trash2 className="h-4 w-4" />
															</Button>
														</AlertDialogTrigger>
														<AlertDialogContent>
															<AlertDialogHeader>
																<AlertDialogTitle>
																	{t("project.deleteMilestone")}
																</AlertDialogTitle>
																<AlertDialogDescription>
																	{t("project.confirmDeleteMilestone")}
																</AlertDialogDescription>
															</AlertDialogHeader>
															<AlertDialogFooter>
																<AlertDialogCancel>
																	{t("common.cancel")}
																</AlertDialogCancel>
																<AlertDialogAction
																	onClick={() => handleDeleteMilestone(m.id)}
																	className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
																>
																	{t("common.delete")}
																</AlertDialogAction>
															</AlertDialogFooter>
														</AlertDialogContent>
													</AlertDialog>
												</div>

												{m.paymentStatus !== "paid" &&
													m.fundingStatus !== "funded" && (
														<StudioButton
															variant="primary"
															size="sm"
															onClick={() => handleMarkPaid(m.id)}
														>
															Mark Paid
														</StudioButton>
													)}

												{done &&
													m.fundingStatus === "funded" &&
													userType === "homeowner" &&
													onReleasePayment && (
														<div className="flex flex-col items-end gap-2">
															{releaseErrors?.[m.id] && (
																<div className="max-w-xs rounded-md border border-dashboard-danger-border bg-dashboard-danger-bg px-3 py-2 text-xs text-dashboard-danger">
																	{releaseErrors[m.id]}
																</div>
															)}
															<AlertDialog>
																<AlertDialogTrigger asChild>
																	<Button
																		variant="default"
																		size="sm"
																		disabled={releaseLoading === m.id}
																	>
																		{releaseLoading === m.id
																			? "Releasing..."
																			: "Release Escrow"}
																	</Button>
																</AlertDialogTrigger>
																<AlertDialogContent>
																	<AlertDialogHeader>
																		<AlertDialogTitle>
																			Release Payment?
																		</AlertDialogTitle>
																		<AlertDialogDescription>
																			Are you sure you want to release the
																			escrow payout of{" "}
																			{formatPrice(m.paymentAmount ?? 0)} to the
																			studio account?
																		</AlertDialogDescription>
																	</AlertDialogHeader>
																	<AlertDialogFooter>
																		<AlertDialogCancel>
																			{t("common.cancel")}
																		</AlertDialogCancel>
																		<AlertDialogAction
																			onClick={() => onReleasePayment(m.id)}
																		>
																			Release
																		</AlertDialogAction>
																	</AlertDialogFooter>
																</AlertDialogContent>
															</AlertDialog>
														</div>
													)}
											</div>
										)}
									</FloatPanel>
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}

// ============================================================================
// COSTS TAB
// ============================================================================

function CostsTab({
	projectId,
	costs,
	budgetMax,
	onRefresh,
}: {
	projectId: string;
	costs: CostEstimate[];
	budgetMax?: number;
	onRefresh: () => void;
}): React.ReactElement {
	const { t } = useTranslation();
	const [showForm, setShowForm] = useState(false);
	const [newCategory, setNewCategory] =
		useState<CostEstimate["category"]>("design_fees");
	const [newDescription, setNewDescription] = useState("");
	const [newAmount, setNewAmount] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [editingCostId, setEditingCostId] = useState<string | null>(null);
	const [editDescription, setEditDescription] = useState("");
	const [editEstimated, setEditEstimated] = useState("");
	const [editActual, setEditActual] = useState("");
	const [editSaving, setEditSaving] = useState(false);

	const totalEstimated = costs.reduce((sum, c) => sum + c.estimatedAmount, 0);
	const totalActual = costs.reduce((sum, c) => sum + (c.actualAmount ?? 0), 0);

	const budgetRatio =
		budgetMax && budgetMax > 0 ? totalEstimated / budgetMax : 0;
	const budgetStatus: "over" | "near" | "within" =
		budgetRatio >= 1 ? "over" : budgetRatio >= 0.8 ? "near" : "within";
	const budgetBarColor =
		budgetStatus === "over"
			? "bg-dashboard-danger-fill"
			: budgetStatus === "near"
				? "bg-yellow-500"
				: "bg-dashboard-success-fill";

	const costsByCategory = COST_CATEGORIES.map((cat) => {
		const items = costs.filter((c) => c.category === cat.value);
		const subtotal = items.reduce((sum, c) => sum + c.estimatedAmount, 0);
		return { ...cat, label: t(cat.labelKey), items, subtotal };
	}).filter((group) => group.items.length > 0);

	const handleCreateCost = async () => {
		// Previously a silent early-return — the Add button appeared to do nothing
		// on blank/invalid input. Tell the user what's wrong instead.
		const amount = Number.parseFloat(newAmount);
		if (!newDescription.trim()) {
			toast.error("Enter a description for this cost");
			return;
		}
		if (!Number.isFinite(amount) || amount <= 0) {
			// isFinite rejects NaN and Infinity (parseFloat("Infinity") -> Infinity).
			toast.error("Enter a valid amount greater than 0");
			return;
		}
		setIsSubmitting(true);
		try {
			const result = await createCostEstimate(projectId, {
				category: newCategory,
				description: newDescription.trim(),
				estimatedAmount: amount,
			});
			if (result.success) {
				setNewDescription("");
				setNewAmount("");
				setNewCategory("design_fees");
				setShowForm(false);
				onRefresh();
			} else {
				toast.error(result.error || t("common.somethingWentWrong"));
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const startEditCost = (cost: CostEstimate) => {
		setEditingCostId(cost.id);
		setEditDescription(cost.description);
		setEditEstimated(String(cost.estimatedAmount));
		setEditActual(cost.actualAmount != null ? String(cost.actualAmount) : "");
	};

	const handleSaveEdit = async () => {
		if (!editingCostId || !editDescription.trim() || !editEstimated) return;
		setEditSaving(true);
		try {
			const result = await updateCostEstimate(editingCostId, {
				description: editDescription.trim(),
				estimatedAmount: parseFloat(editEstimated),
				actualAmount: editActual ? parseFloat(editActual) : null,
			});
			if (result.success) {
				setEditingCostId(null);
				onRefresh();
				toast.success(t("common.saveChanges"));
			} else {
				toast.error(result.error || t("common.somethingWentWrong"));
			}
		} finally {
			setEditSaving(false);
		}
	};

	const handleDeleteCost = async (costId: string) => {
		const result = await deleteCostEstimate(costId);
		if (result.success) {
			onRefresh();
		} else {
			toast.error(result.error || t("common.somethingWentWrong"));
		}
	};

	return (
		<div
			className="space-y-6"
			style={{
				background: "var(--color-dashboard-bg)",
				borderRadius: 11,
				padding: 24,
			}}
		>
			<div className="flex items-center justify-between">
				<div>
					<p
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 8.5,
							letterSpacing: "0.12em",
							color: "var(--color-dashboard-muted)",
							textTransform: "uppercase",
						}}
					>
						{t("project.costEstimates")} · {costs.length}
					</p>
					<h2
						style={{
							fontFamily: "var(--font-display)",
							fontStyle: "italic",
							fontWeight: 500,
							fontSize: 28,
							color: "var(--color-dashboard-text)",
							letterSpacing: "-0.02em",
							marginTop: 6,
						}}
					>
						Where the budget{" "}
						<span style={{ color: "var(--color-dashboard-accent-display)" }}>
							lives
						</span>
						.
					</h2>
				</div>
				<StudioButton
					variant="secondary"
					size="sm"
					icon={Plus}
					onClick={() => setShowForm(!showForm)}
				>
					{t("project.addCost")}
				</StudioButton>
			</div>

			{/* Budget Comparison Summary */}
			<div
				style={{
					background: "var(--color-dashboard-warm-450)",
					borderRadius: 10,
					border: "1px solid var(--color-dashboard-border)",
					padding: 20,
				}}
			>
				<div className="grid grid-cols-3 gap-4">
					<div>
						<p
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 8.5,
								letterSpacing: "0.12em",
								color: "var(--color-dashboard-muted)",
								textTransform: "uppercase",
							}}
						>
							{t("project.totalEstimated")}
						</p>
						<p
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 28,
								color: "var(--color-dashboard-text)",
								letterSpacing: "-0.02em",
								marginTop: 6,
							}}
						>
							{formatPrice(totalEstimated)}
						</p>
					</div>
					<div>
						<p
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 8.5,
								letterSpacing: "0.12em",
								color: "var(--color-dashboard-muted)",
								textTransform: "uppercase",
							}}
						>
							{t("project.totalActual")}
						</p>
						<p
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 28,
								color:
									totalActual > 0
										? "var(--color-dashboard-accent-dark)"
										: "var(--color-dashboard-text)",
								letterSpacing: "-0.02em",
								marginTop: 6,
							}}
						>
							{totalActual > 0
								? formatPrice(totalActual)
								: t("project.noActualYet")}
						</p>
					</div>
					{budgetMax != null && budgetMax > 0 && (
						<div>
							<p
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 8.5,
									letterSpacing: "0.12em",
									color: "var(--color-dashboard-muted)",
									textTransform: "uppercase",
								}}
							>
								{t("project.budgetMax")}
							</p>
							<p
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 500,
									fontSize: 28,
									color: "var(--color-dashboard-text)",
									letterSpacing: "-0.02em",
									marginTop: 6,
								}}
							>
								{formatPrice(budgetMax)}
							</p>
						</div>
					)}
				</div>

				{budgetMax != null && budgetMax > 0 && (
					<div style={{ marginTop: 16 }}>
						<div className="flex items-center justify-between text-sm mb-2">
							<span
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 8.5,
									letterSpacing: "0.1em",
									color: "var(--color-dashboard-muted)",
									textTransform: "uppercase",
								}}
							>
								{Math.round(budgetRatio * 100)}% {t("project.ofBudget")}
							</span>
							<span
								style={{
									fontWeight: 600,
									fontSize: 12,
									color:
										budgetStatus === "over"
											? "var(--color-dashboard-pill-risk)"
											: budgetStatus === "near"
												? "var(--color-dashboard-pill-awaiting)"
												: "var(--color-dashboard-pill-done)",
								}}
							>
								{t(
									`project.${budgetStatus === "over" ? "overBudget" : budgetStatus === "near" ? "nearBudget" : "withinBudget"}`,
								)}
							</span>
						</div>
						<div className="h-2 w-full overflow-hidden rounded-full bg-dashboard-border">
							<div
								className="h-full rounded-full transition-all"
								style={{
									width: `${Math.min(100, budgetRatio * 100)}%`,
									background: budgetBarColor,
								}}
							/>
						</div>
					</div>
				)}

				{budgetStatus !== "within" && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							marginTop: 16,
							padding: "12px 14px",
							borderRadius: 7,
							fontSize: 13,
							background:
								budgetStatus === "over"
									? "var(--color-dashboard-pill-risk-bg)"
									: "var(--color-dashboard-pill-awaiting-bg)",
							color:
								budgetStatus === "over"
									? "var(--color-dashboard-pill-risk)"
									: "var(--color-dashboard-pill-awaiting)",
						}}
					>
						<AlertTriangle className="h-4 w-4 shrink-0" />
						<span>
							{budgetStatus === "over"
								? t("project.overBudget")
								: t("project.nearBudget")}
							{" - "}
							{formatPrice(totalEstimated)} / {formatPrice(budgetMax ?? 0)}
						</span>
					</div>
				)}

				{totalActual > 0 && (
					<div className="flex items-center gap-2 text-sm mt-4">
						<span
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 8.5,
								letterSpacing: "0.1em",
								color: "var(--color-dashboard-muted)",
								textTransform: "uppercase",
							}}
						>
							{t("project.variance")}:
						</span>
						<span
							style={{
								fontWeight: 600,
								fontSize: 13,
								color:
									totalActual > totalEstimated
										? "var(--color-dashboard-pill-risk)"
										: "var(--color-dashboard-pill-done)",
							}}
						>
							{totalActual > totalEstimated ? "+" : "-"}
							{formatPrice(Math.abs(totalActual - totalEstimated))}{" "}
							{totalActual > totalEstimated
								? t("project.overAmount")
								: t("project.underBudget")}
						</span>
					</div>
				)}
			</div>

			{showForm && (
				<div
					style={{
						background: "var(--color-dashboard-warm-450)",
						borderRadius: 11,
						border: "1px solid var(--color-dashboard-border)",
						padding: 24,
						borderLeft: "3px solid var(--color-dashboard-accent)",
					}}
				>
					<p
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 8.5,
							letterSpacing: "0.12em",
							color: "var(--color-dashboard-muted)",
							textTransform: "uppercase",
							marginBottom: 16,
						}}
					>
						{t("project.newCostEstimate")}
					</p>
					<div className="space-y-4">
						<div>
							<Label
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 8.5,
									letterSpacing: "0.1em",
									color: "var(--color-dashboard-muted)",
									textTransform: "uppercase",
									marginBottom: 6,
									display: "block",
								}}
							>
								{t("project.category")}
							</Label>
							<Select
								value={newCategory}
								onValueChange={(val) =>
									setNewCategory(val as CostEstimate["category"])
								}
							>
								<SelectTrigger
									className="w-full"
									style={{
										height: 40,
										borderRadius: 7,
										border: "1px solid var(--color-dashboard-progress-empty)",
										background: "var(--color-dashboard-warm-450)",
									}}
								>
									<SelectValue placeholder={t("project.selectCategory")} />
								</SelectTrigger>
								<SelectContent>
									{COST_CATEGORIES.map((cat) => (
										<SelectItem key={cat.value} value={cat.value}>
											{t(cat.labelKey)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div>
							<Label
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 8.5,
									letterSpacing: "0.1em",
									color: "var(--color-dashboard-muted)",
									textTransform: "uppercase",
									marginBottom: 6,
									display: "block",
								}}
							>
								{t("project.description")}
							</Label>
							<Input
								value={newDescription}
								onChange={(e) => setNewDescription(e.target.value)}
								placeholder={t("project.costDescPlaceholder")}
								style={{
									height: 40,
									borderRadius: 7,
									border: "1px solid var(--color-dashboard-progress-empty)",
									background: "var(--color-dashboard-warm-450)",
								}}
							/>
						</div>
						<div>
							<Label
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 8.5,
									letterSpacing: "0.1em",
									color: "var(--color-dashboard-muted)",
									textTransform: "uppercase",
									marginBottom: 6,
									display: "block",
								}}
							>
								{t("project.amountRs")}
							</Label>
							<Input
								type="number"
								value={newAmount}
								onChange={(e) => setNewAmount(e.target.value)}
								placeholder={t("project.amountPlaceholder")}
								style={{
									height: 40,
									borderRadius: 7,
									border: "1px solid var(--color-dashboard-progress-empty)",
									background: "var(--color-dashboard-warm-450)",
								}}
							/>
							{newAmount && !Number.isNaN(parseFloat(newAmount)) && (
								<p
									style={{
										marginTop: 6,
										fontSize: 11,
										color: "var(--color-dashboard-muted)",
									}}
								>
									{formatPrice(parseFloat(newAmount))}
								</p>
							)}
						</div>
					</div>
					<div className="flex gap-2 mt-4">
						<StudioButton
							variant="primary"
							size="sm"
							onClick={handleCreateCost}
							disabled={isSubmitting}
						>
							{t("project.addCost")}
						</StudioButton>
						<StudioButton
							variant="ghost"
							size="sm"
							onClick={() => {
								setShowForm(false);
								setNewDescription("");
								setNewAmount("");
								setNewCategory("design_fees");
							}}
						>
							{t("common.cancel")}
						</StudioButton>
					</div>
				</div>
			)}

			{/* Cost Groups */}
			{costsByCategory.length === 0 ? (
				<div
					style={{
						background: "var(--color-dashboard-warm-450)",
						borderRadius: 10,
						border: "1px solid var(--color-dashboard-border)",
						padding: 32,
						textAlign: "center",
					}}
				>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 13.5,
							color: "var(--color-dashboard-muted-light)",
							fontStyle: "italic",
						}}
					>
						{t("project.noCostEstimatesYet")}
					</p>
				</div>
			) : (
				costsByCategory.map((group) => (
					<div
						key={group.value}
						style={{
							background: "var(--color-dashboard-warm-450)",
							borderRadius: 10,
							border: "1px solid var(--color-dashboard-border)",
							overflow: "hidden",
						}}
					>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "baseline",
								padding: "16px 20px",
								borderBottom: "1px solid var(--color-dashboard-divider)",
							}}
						>
							<span
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 18,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
								}}
							>
								{group.label}
							</span>
							<span
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 500,
									fontSize: 16,
									color: "var(--color-dashboard-text)",
								}}
							>
								{formatPrice(group.subtotal)}
							</span>
						</div>
						<div>
							{group.items.map((cost) => (
								<div
									key={cost.id}
									style={{
										padding: "16px 20px",
										borderBottom:
											group.items.indexOf(cost) < group.items.length - 1
												? "1px solid var(--color-dashboard-divider)"
												: "none",
									}}
								>
									{editingCostId === cost.id ? (
										/* Inline edit form */
										<div className="space-y-3">
											<div>
												<Label
													style={{
														fontFamily: "var(--font-mono)",
														fontSize: 8.5,
														letterSpacing: "0.1em",
														color: "var(--color-dashboard-muted)",
														textTransform: "uppercase",
														marginBottom: 6,
														display: "block",
													}}
												>
													{t("project.description")}
												</Label>
												<Input
													value={editDescription}
													onChange={(e) => setEditDescription(e.target.value)}
													style={{
														height: 36,
														borderRadius: 6,
														border:
															"1px solid var(--color-dashboard-progress-empty)",
														background: "var(--color-dashboard-warm-450)",
														fontSize: 14,
													}}
												/>
											</div>
											<div className="grid grid-cols-2 gap-3">
												<div>
													<Label
														style={{
															fontFamily: "var(--font-mono)",
															fontSize: 8.5,
															letterSpacing: "0.1em",
															color: "var(--color-dashboard-muted)",
															textTransform: "uppercase",
															marginBottom: 6,
															display: "block",
														}}
													>
														{t("project.estimated")}
													</Label>
													<Input
														type="number"
														value={editEstimated}
														onChange={(e) => setEditEstimated(e.target.value)}
														style={{
															height: 36,
															borderRadius: 6,
															border:
																"1px solid var(--color-dashboard-progress-empty)",
															background: "var(--color-dashboard-warm-450)",
															fontSize: 14,
														}}
														min={0}
													/>
												</div>
												<div>
													<Label
														style={{
															fontFamily: "var(--font-mono)",
															fontSize: 8.5,
															letterSpacing: "0.1em",
															color: "var(--color-dashboard-muted)",
															textTransform: "uppercase",
															marginBottom: 6,
															display: "block",
														}}
													>
														{t("project.actualAmount")}
													</Label>
													<Input
														type="number"
														value={editActual}
														onChange={(e) => setEditActual(e.target.value)}
														style={{
															height: 36,
															borderRadius: 6,
															border:
																"1px solid var(--color-dashboard-progress-empty)",
															background: "var(--color-dashboard-warm-450)",
															fontSize: 14,
														}}
														placeholder={t("project.enterActual")}
														min={0}
													/>
												</div>
											</div>
											<div className="flex gap-2">
												<StudioButton
													variant="primary"
													size="sm"
													onClick={handleSaveEdit}
													disabled={editSaving}
												>
													{t("common.save")}
												</StudioButton>
												<StudioButton
													variant="ghost"
													size="sm"
													onClick={() => setEditingCostId(null)}
												>
													{t("common.cancel")}
												</StudioButton>
											</div>
										</div>
									) : (
										/* Display row */
										<div className="flex items-center justify-between">
											<div className="min-w-0 flex-1">
												<p
													style={{
														fontFamily: "var(--font-body)",
														fontSize: 14,
														fontWeight: 600,
														color: "var(--color-dashboard-text)",
													}}
												>
													{cost.description}
												</p>
												<div
													style={{
														display: "flex",
														flexWrap: "wrap",
														columnGap: 16,
														rowGap: 2,
														marginTop: 4,
														fontSize: 11,
														color: "var(--color-dashboard-muted)",
													}}
												>
													<span>{formatDate(cost.createdAt)}</span>
													{cost.actualAmount != null && (
														<span
															style={{
																fontWeight: 600,
																color:
																	cost.actualAmount > cost.estimatedAmount
																		? "var(--color-dashboard-pill-risk)"
																		: "var(--color-dashboard-pill-done)",
															}}
														>
															{t("project.actual")}:{" "}
															{formatPrice(cost.actualAmount)}
														</span>
													)}
												</div>
											</div>
											<div className="flex shrink-0 items-center gap-3">
												<div className="text-right">
													<p
														style={{
															fontFamily: "var(--font-display)",
															fontWeight: 500,
															fontSize: 15,
															color: "var(--color-dashboard-text)",
														}}
													>
														{formatPrice(cost.estimatedAmount)}
													</p>
													{cost.actualAmount != null && (
														<p
															style={{
																fontSize: 11,
																fontWeight: 600,
																color:
																	cost.actualAmount > cost.estimatedAmount
																		? "var(--color-dashboard-pill-risk)"
																		: "var(--color-dashboard-pill-done)",
															}}
														>
															{cost.actualAmount > cost.estimatedAmount
																? "+"
																: "-"}
															{formatPrice(
																Math.abs(
																	cost.actualAmount - cost.estimatedAmount,
																),
															)}
														</p>
													)}
												</div>
												<StudioButton
													variant="ghost"
													size="sm"
													icon={Pencil}
													onClick={() => startEditCost(cost)}
													title={t("project.editCost")}
												/>
												<AlertDialog>
													<AlertDialogTrigger asChild>
														<Button
															variant="ghost"
															size="sm"
															title={t("project.deleteCost")}
														>
															<Trash2 className="h-4 w-4 text-destructive" />
														</Button>
													</AlertDialogTrigger>
													<AlertDialogContent>
														<AlertDialogHeader>
															<AlertDialogTitle>
																{t("project.deleteCost")}
															</AlertDialogTitle>
															<AlertDialogDescription>
																{t("project.confirmDeleteCost")}
															</AlertDialogDescription>
														</AlertDialogHeader>
														<AlertDialogFooter>
															<AlertDialogCancel>
																{t("common.cancel")}
															</AlertDialogCancel>
															<AlertDialogAction
																onClick={() => handleDeleteCost(cost.id)}
																className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
															>
																{t("common.delete")}
															</AlertDialogAction>
														</AlertDialogFooter>
													</AlertDialogContent>
												</AlertDialog>
											</div>
										</div>
									)}
								</div>
							))}
						</div>
					</div>
				))
			)}
		</div>
	);
}

// ============================================================================
// PAYMENTS TAB
// ============================================================================

function PaymentsTab({
	projectId,
	transactions,
	summary,
}: {
	projectId: string;
	transactions: EscrowTransaction[];
	summary: PaymentsSummary;
}): React.ReactElement {
	const { t } = useTranslation();

	return (
		<div
			className="space-y-6"
			style={{
				background: "var(--color-dashboard-bg)",
				borderRadius: 11,
				padding: 24,
			}}
		>
			<div
				style={{
					background: "var(--color-dashboard-warm-450)",
					borderRadius: 10,
					border: "1px solid var(--color-dashboard-border)",
					padding: 24,
				}}
			>
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<div>
						<p
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontSize: 20,
								fontWeight: 500,
								color: "var(--color-dashboard-text)",
								letterSpacing: "-0.01em",
							}}
						>
							{t("project.payments.title")}
						</p>
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 12.5,
								color: "var(--color-dashboard-muted)",
								marginTop: 4,
							}}
						>
							Share a secure Finishd payment request link.
						</p>
					</div>
					<ShareAction
						artifact={{
							type: "payment_request",
							id: projectId,
							projectId,
						}}
						label="Share payment request"
					/>
				</div>
			</div>

			{/* Summary Cards */}
			<div className="grid grid-cols-3 gap-4">
				<div
					style={{
						background: "var(--color-dashboard-warm-450)",
						borderRadius: 10,
						border: "1px solid var(--color-dashboard-border)",
						padding: 20,
						textAlign: "center",
					}}
				>
					<p
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 8.5,
							letterSpacing: "0.12em",
							color: "var(--color-dashboard-muted)",
							textTransform: "uppercase",
						}}
					>
						{t("project.payments.totalFunded")}
					</p>
					<p
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 22,
							color: "var(--color-dashboard-text)",
							letterSpacing: "-0.015em",
							marginTop: 6,
						}}
					>
						{formatPrice(summary.totalFunded)}
					</p>
				</div>
				<div
					style={{
						background: "var(--color-dashboard-warm-450)",
						borderRadius: 10,
						border: "1px solid var(--color-dashboard-border)",
						padding: 20,
						textAlign: "center",
					}}
				>
					<p
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 8.5,
							letterSpacing: "0.12em",
							color: "var(--color-dashboard-muted)",
							textTransform: "uppercase",
						}}
					>
						{t("project.payments.totalHeld")}
					</p>
					<p
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 22,
							color: "var(--color-dashboard-text)",
							letterSpacing: "-0.015em",
							marginTop: 6,
						}}
					>
						{formatPrice(summary.totalHeld)}
					</p>
					{/* `getProjectPayments` derives this as funded - released -
					    refunded and does not clamp it, so it is negative once a
					    contractor is paid past the funding. */}
					{summary.totalHeld < 0 && (
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 11,
								color: "var(--color-dashboard-muted)",
								marginTop: 4,
							}}
						>
							{OVERDRAWN_ESCROW_NOTE}
						</p>
					)}
				</div>
				<div
					style={{
						background: "var(--color-dashboard-warm-450)",
						borderRadius: 10,
						border: "1px solid var(--color-dashboard-border)",
						padding: 20,
						textAlign: "center",
					}}
				>
					<p
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 8.5,
							letterSpacing: "0.12em",
							color: "var(--color-dashboard-muted)",
							textTransform: "uppercase",
						}}
					>
						{t("project.payments.totalReleased")}
					</p>
					<p
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 22,
							color: "var(--color-dashboard-text)",
							letterSpacing: "-0.015em",
							marginTop: 6,
						}}
					>
						{formatPrice(summary.totalReleased)}
					</p>
				</div>
			</div>

			{/* Transaction List */}
			{transactions.length === 0 ? (
				<div
					style={{
						background: "var(--color-dashboard-warm-450)",
						borderRadius: 10,
						border: "1px solid var(--color-dashboard-border)",
						padding: 48,
						textAlign: "center",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: 12,
					}}
				>
					<IndianRupee
						className="h-10 w-10"
						style={{
							color:
								"color-mix(in srgb, var(--color-dashboard-muted) 40%, transparent)",
						}}
					/>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 13.5,
							color: "var(--color-dashboard-muted)",
						}}
					>
						{t("project.payments.noPaymentsYet")}
					</p>
				</div>
			) : (
				<div
					style={{
						background: "var(--color-dashboard-warm-450)",
						borderRadius: 10,
						border: "1px solid var(--color-dashboard-border)",
						overflow: "hidden",
					}}
				>
					{transactions.map((txn, index) => (
						<div
							key={txn.id}
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								padding: "16px 20px",
								borderBottom:
									index < transactions.length - 1
										? "1px solid var(--color-dashboard-divider)"
										: "none",
							}}
						>
							<div>
								<p
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 14,
										fontWeight: 600,
										color: "var(--color-dashboard-text)",
									}}
								>
									{/* A contractor assignment names no milestone and is not
									    funding: it is paid out of the funded escrow, and Total
									    Released counts it once it is. */}
									{isContractorAssignment(txn)
										? CONTRACTOR_PAYOUT_TITLE
										: txn.milestoneTitle || "Project Funding"}
								</p>
								<p
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 9,
										letterSpacing: "0.1em",
										color: "var(--color-dashboard-muted)",
										textTransform: "uppercase",
										marginTop: 4,
									}}
								>
									{formatDate(txn.createdAt)}
								</p>
							</div>
							<div className="flex items-center gap-3">
								<span
									style={{
										fontFamily: "var(--font-display)",
										fontWeight: 500,
										fontSize: 15,
										color: "var(--color-dashboard-text)",
									}}
								>
									{formatPrice(txn.amount)}
								</span>
								{/* A contractor assignment at `held` is not in Total Held --
								    its money is already there as part of what was funded --
								    so it is labelled as a payout not yet made. */}
								<StatusBadge
									status={txn.status}
									label={
										isContractorAssignment(txn) && txn.status === "held"
											? AWAITING_PAYOUT_LABEL
											: undefined
									}
								/>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

// ============================================================================
// ACTIVITY TAB
// ============================================================================

function ActivityTab({
	projectId,
	activities,
}: {
	projectId: string;
	activities: ActivityLog[];
}): React.ReactElement {
	const { t } = useTranslation();
	return (
		<div
			className="space-y-6"
			style={{
				background: "var(--color-dashboard-bg)",
				borderRadius: 11,
				padding: 24,
			}}
		>
			<ShareHistory projectId={projectId} />
			<div>
				<p
					style={{
						fontFamily: "var(--font-mono)",
						fontSize: 8.5,
						letterSpacing: "0.12em",
						color: "var(--color-dashboard-muted)",
						textTransform: "uppercase",
					}}
				>
					{t("project.activity")} · {activities.length}
				</p>
				<h2
					style={{
						fontFamily: "var(--font-display)",
						fontStyle: "italic",
						fontWeight: 500,
						fontSize: 28,
						color: "var(--color-dashboard-text)",
						letterSpacing: "-0.02em",
						marginTop: 6,
					}}
				>
					The whole{" "}
					<span style={{ color: "var(--color-dashboard-accent-display)" }}>
						story
					</span>
					.
				</h2>
			</div>

			{activities.length === 0 ? (
				<div
					style={{
						background: "var(--color-dashboard-warm-450)",
						borderRadius: 10,
						border: "1px solid var(--color-dashboard-border)",
						padding: 32,
						textAlign: "center",
					}}
				>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 13.5,
							color: "var(--color-dashboard-muted-light)",
							fontStyle: "italic",
						}}
					>
						{t("project.noActivityYet")}
					</p>
				</div>
			) : (
				<div
					style={{
						position: "relative",
						paddingLeft: 34,
						maxWidth: 760,
					}}
				>
					<div
						style={{
							position: "absolute",
							left: 15,
							top: 8,
							bottom: 8,
							width: 2,
							background: "var(--color-dashboard-border)",
						}}
					/>
					<div className="space-y-4">
						{activities.map((log, _index) => (
							<div
								key={log.id}
								style={{
									position: "relative",
									padding: "12px 0",
								}}
							>
								<span
									style={{
										position: "absolute",
										left: -34,
										top: 12,
										width: 32,
										height: 32,
										borderRadius: "50%",
										background: "var(--color-dashboard-bg)",
										boxShadow:
											"0 0 0 4px var(--color-dashboard-surface), inset 0 0 0 1px var(--color-dashboard-border)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										zIndex: 1,
									}}
								>
									<span
										style={{
											width: 8,
											height: 8,
											borderRadius: "50%",
											background: "var(--color-dashboard-accent)",
										}}
									/>
								</span>
								<div className="flex items-center gap-3">
									<div
										style={{
											width: 32,
											height: 32,
											borderRadius: 7,
											background: "var(--color-dashboard-warm-200)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											color: "var(--color-dashboard-accent-dark)",
											fontSize: 12,
										}}
									>
										<User style={{ width: 14, height: 14 }} />
									</div>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 13.5,
											color: "var(--color-dashboard-text)",
											lineHeight: 1.5,
										}}
									>
										<strong>{log.action}</strong>
									</p>
								</div>
								{log.details && (
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 12,
											color: "var(--color-dashboard-muted)",
											marginTop: 4,
											marginLeft: 50,
										}}
									>
										{typeof log.details === "string"
											? log.details
											: Object.entries(log.details as Record<string, unknown>)
													.map(([k, v]) => `${k}: ${String(v)}`)
													.join(", ")}
									</p>
								)}
								<p
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 8.5,
										letterSpacing: "0.1em",
										color: "var(--color-dashboard-muted-light)",
										marginTop: 5,
										marginLeft: 50,
										textTransform: "uppercase",
									}}
								>
									{formatDate(log.createdAt)}
								</p>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

// ============================================================================
// TEAM TAB
// ============================================================================

function TeamTab({
	project,
	projectId,
	contractors,
	onRefresh,
}: {
	project: Project;
	projectId: string;
	contractors: ProjectContractorItem[];
	onRefresh: () => void;
}): React.ReactElement {
	const { t } = useTranslation();
	const [showInvite, setShowInvite] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	// Autocomplete suggestions (shown while typing)
	const [suggestions, setSuggestions] = useState<ContractorProfile[]>([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [loadingSuggestions, setLoadingSuggestions] = useState(false);
	// Explicit search results (shown after clicking Search)
	const [searchResults, setSearchResults] = useState<ContractorProfile[]>([]);
	const [searching, setSearching] = useState(false);
	const [hasSearched, setHasSearched] = useState(false);
	const [searchFailed, setSearchFailed] = useState(false);
	const [loadingDefault, setLoadingDefault] = useState(false);
	const [inviting, setInviting] = useState<string | null>(null);
	const [actionLoading, setActionLoading] = useState<string | null>(null);
	const queryClient = useQueryClient();
	const { data: paymentsData } = useProjectPayments(projectId);
	const { data: contractorsData } = useProjectContractors(projectId);
	const disputeWindowHours = contractorsData?.disputeWindowHours ?? 72;
	// Derived from the payments query alone, so between a successful
	// authorization and the refetch that reflects it the card would still offer
	// "Authorize Payment" for an escrow already released. An acceptance this
	// component saw does not stop being true, so it is kept until the refetch.
	const releasedByPayments = useMemo(
		() => releasedAssignmentIdsFrom(paymentsData?.transactions),
		[paymentsData],
	);
	const [authorizedIds, setAuthorizedIds] = useState<ReadonlySet<string>>(
		() => new Set(),
	);

	// A removed or declined contractor can be invited again, so only the
	// contractors still on the project are hidden from the invite search.
	const existingIds = useMemo(
		() =>
			new Set(
				contractors
					.filter((c) => c.status !== "removed" && c.status !== "declined")
					.map((c) => c.contractorId),
			),
		[contractors],
	);

	// Debounced autocomplete — 200 ms after keystroke
	useEffect(() => {
		if (!searchQuery.trim()) {
			setSuggestions([]);
			setShowSuggestions(false);
			return;
		}
		setLoadingSuggestions(true);
		const timer = setTimeout(async () => {
			try {
				const result = await fetchContractors({
					query: searchQuery.trim(),
					limit: 8,
				});
				const filtered = result.contractors.filter(
					(c) => !existingIds.has(c.id),
				);
				setSuggestions(filtered);
				setShowSuggestions(filtered.length > 0);
			} catch {
				setShowSuggestions(false);
			} finally {
				setLoadingSuggestions(false);
			}
		}, 200);
		return () => clearTimeout(timer);
	}, [searchQuery, existingIds]);

	const handleSearch = async () => {
		if (!searchQuery.trim()) return;
		setShowSuggestions(false);
		setSearching(true);
		setHasSearched(true);
		setSearchFailed(false);
		try {
			const result = await fetchContractors({ query: searchQuery.trim() });
			setSearchResults(
				result.contractors.filter((c) => !existingIds.has(c.id)),
			);
		} catch {
			setSearchFailed(true);
		} finally {
			setSearching(false);
		}
	};

	// Opening the panel lists verified contractors straight away, so a project
	// can be staffed by browsing rather than by already knowing a name.
	const loadDefaultContractors = useCallback(async () => {
		setLoadingDefault(true);
		setSearchFailed(false);
		try {
			const result = await fetchContractors({ limit: 12 });
			setSearchResults(
				result.contractors.filter((c) => !existingIds.has(c.id)),
			);
		} catch {
			setSearchFailed(true);
		} finally {
			setLoadingDefault(false);
		}
	}, [existingIds]);

	useEffect(() => {
		if (showInvite && !hasSearched) void loadDefaultContractors();
	}, [showInvite, hasSearched, loadDefaultContractors]);

	const selectSuggestion = (c: ContractorProfile) => {
		setSearchQuery(c.name);
		setShowSuggestions(false);
		setSuggestions([]);
	};

	const activeContractors = contractors.filter(
		(c) => c.status !== "removed" && c.status !== "declined",
	);

	const handleInvite = async (contractorId: string) => {
		setInviting(contractorId);
		try {
			const result = await inviteContractor(projectId, contractorId);
			if (result.success) {
				setSearchResults((prev) => prev.filter((c) => c.id !== contractorId));
				toast.success("Contractor invited");
				onRefresh();
			} else {
				// Previously silent — the user saw "nothing happens" on failure.
				toast.error(result.error ?? "Failed to invite contractor");
			}
		} finally {
			setInviting(null);
		}
	};

	const handleHire = async (invitationId: string) => {
		setActionLoading(invitationId);
		try {
			const result = await hireContractor(invitationId);
			if (result.success) {
				toast.success("Contractor hired");
				onRefresh();
			} else {
				toast.error(result.error ?? "Failed to hire contractor");
			}
		} finally {
			setActionLoading(null);
		}
	};

	const handleRemove = async (invitationId: string) => {
		setActionLoading(invitationId);
		try {
			const result = await removeContractor(invitationId);
			if (result.success) {
				toast.success("Contractor removed");
				onRefresh();
			} else {
				toast.error(result.error ?? "Failed to remove contractor");
			}
		} finally {
			setActionLoading(null);
		}
	};

	const handleConfirmCompletion = async (assignmentId: string) => {
		setActionLoading(assignmentId);
		try {
			const result = await confirmCompletion(assignmentId);
			if (result.success) {
				toast.success("Completion confirmed — contractor has been notified");
				onRefresh();
			} else {
				toast.error(result.error ?? "Failed to confirm completion");
			}
		} finally {
			setActionLoading(null);
		}
	};

	const handleAuthorizePayment = async (assignmentId: string) => {
		setActionLoading(assignmentId);
		try {
			const result = await authorizeAssignmentPayment(assignmentId);
			if (result.success) {
				toast.success(
					"Payment authorized — contractor will receive their payout",
				);
				setAuthorizedIds((prev) => new Set(prev).add(assignmentId));
				void queryClient.invalidateQueries({
					queryKey: queryKeys.projects.payments(projectId),
				});
				onRefresh();
			} else {
				toast.error(result.error ?? "Failed to authorize payment");
			}
		} finally {
			setActionLoading(null);
		}
	};

	const handleDisputeCompletion = async (assignmentId: string) => {
		setActionLoading(assignmentId);
		try {
			const result = await disputeCompletion(assignmentId);
			if (result.success) {
				toast.success(
					"Assignment disputed and reopened — contractor has been notified",
				);
				onRefresh();
			} else {
				toast.error(result.error ?? "Failed to dispute completion");
			}
		} finally {
			setActionLoading(null);
		}
	};

	return (
		<FloatPanel elevation={1} padding={24} className="space-y-6">
			{/* Assigned Designer */}
			{project.designerId && (
				<FloatPanel elevation={2} padding={24}>
					<div className="flex items-center gap-3 mb-3">
						<div
							style={{
								width: 40,
								height: 40,
								borderRadius: 8,
								background: "var(--color-dashboard-warm-200)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								color: "var(--color-dashboard-accent-dark)",
							}}
						>
							<User style={{ width: 18, height: 18 }} />
						</div>
						<p
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontSize: 16,
								fontWeight: 500,
								color: "var(--color-dashboard-text)",
							}}
						>
							{t("project.assignedDesigner")}
						</p>
					</div>
					<p
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 9,
							letterSpacing: "0.1em",
							color: "var(--color-dashboard-muted)",
							textTransform: "uppercase",
							marginLeft: 52,
						}}
					>
						{t("project.designerIdLabel", { id: project.designerId })}
					</p>
				</FloatPanel>
			)}

			{/* Contractors */}
			<div>
				<div className="flex items-center justify-between">
					<div>
						<p
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 8.5,
								letterSpacing: "0.12em",
								color: "var(--color-dashboard-muted)",
								textTransform: "uppercase",
							}}
						>
							{t("project.contractors")} · {activeContractors.length}
						</p>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontWeight: 500,
								fontSize: 28,
								color: "var(--color-dashboard-text)",
								letterSpacing: "-0.02em",
								marginTop: 6,
							}}
						>
							Everyone on{" "}
							<span style={{ color: "var(--color-dashboard-accent-display)" }}>
								the team
							</span>
							.
						</h2>
					</div>
					{project.status === "in_progress" && (
						<StudioButton
							variant="secondary"
							size="sm"
							icon={Plus}
							onClick={() => setShowInvite(!showInvite)}
						>
							{t("project.inviteContractor")}
						</StudioButton>
					)}
				</div>

				{/* Invite search */}
				{showInvite && (
					<FloatPanel elevation={2} padding={24} className="mt-4">
						<p
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 8.5,
								letterSpacing: "0.12em",
								color: "var(--color-dashboard-muted)",
								textTransform: "uppercase",
								marginBottom: 16,
							}}
						>
							{t("project.findContractor")}
						</p>

						{/* Input row + autocomplete dropdown */}
						<div style={{ position: "relative", marginBottom: 16 }}>
							<div className="flex gap-2">
								<div style={{ position: "relative", flex: 1 }}>
									<FieldInput
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										placeholder={t("project.searchByNameOrTrade")}
										onKeyDown={(e) => {
											if (e.key === "Enter") handleSearch();
											if (e.key === "Escape") setShowSuggestions(false);
										}}
										autoFocus
									/>
									{/* Spinner inside input */}
									{loadingSuggestions && (
										<Spinner
											label={t("loaders.searchingContractors")}
											className="absolute top-1/2 right-3 size-3.5 -translate-y-1/2"
										/>
									)}
									{/* Autocomplete dropdown */}
									{showSuggestions && suggestions.length > 0 && (
										<div
											style={{
												position: "absolute",
												top: "calc(100% + 6px)",
												left: 0,
												right: 0,
												background: "var(--color-dashboard-warm-450)",
												border: "1px solid var(--color-dashboard-border)",
												borderRadius: 8,
												boxShadow:
													"0 8px 24px -6px color-mix(in srgb, var(--color-dashboard-shadow-ink) 12%, transparent)",
												zIndex: 50,
												overflow: "hidden",
											}}
										>
											{suggestions.map((c, i) => {
												const q = searchQuery.trim().toLowerCase();
												const matchedTrade = c.trades?.find((tr) =>
													tr.toLowerCase().includes(q),
												);
												return (
													<button
														key={c.id}
														type="button"
														onClick={() => selectSuggestion(c)}
														style={{
															display: "flex",
															alignItems: "center",
															gap: 10,
															width: "100%",
															padding: "10px 14px",
															background: "transparent",
															border: "none",
															borderBottom:
																i < suggestions.length - 1
																	? "1px solid var(--color-dashboard-divider)"
																	: "none",
															cursor: "pointer",
															textAlign: "left",
														}}
														onMouseEnter={(e) => {
															e.currentTarget.style.background =
																"var(--color-dashboard-warm-200)";
														}}
														onMouseLeave={(e) => {
															e.currentTarget.style.background = "transparent";
														}}
													>
														<div
															style={{
																width: 28,
																height: 28,
																flexShrink: 0,
																borderRadius: 6,
																background: "var(--color-dashboard-warm-200)",
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																color: "var(--color-dashboard-accent-dark)",
																fontFamily: "var(--font-display)",
																fontStyle: "italic",
																fontSize: 12,
																fontWeight: 600,
															}}
														>
															{c.name.charAt(0).toUpperCase()}
														</div>
														<div style={{ minWidth: 0 }}>
															<p
																style={{
																	fontFamily: "var(--font-display)",
																	fontStyle: "italic",
																	fontSize: 13.5,
																	fontWeight: 500,
																	color: "var(--color-dashboard-text)",
																	whiteSpace: "nowrap",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																}}
															>
																{c.name}
															</p>
															{matchedTrade && (
																<p
																	style={{
																		fontFamily: "var(--font-mono)",
																		fontSize: 9,
																		letterSpacing: "0.08em",
																		color: "var(--color-dashboard-accent-dark)",
																		textTransform: "uppercase",
																		marginTop: 2,
																	}}
																>
																	{matchedTrade}
																</p>
															)}
														</div>
													</button>
												);
											})}
										</div>
									)}
								</div>
								<StudioButton
									variant="secondary"
									size="sm"
									onClick={handleSearch}
									disabled={searching || !searchQuery.trim()}
								>
									{searching ? "Searching…" : t("common.search")}
								</StudioButton>
							</div>
						</div>

						{/* Search results */}
						{showInvite && (
							<div className="space-y-3">
								{searchFailed ? (
									<p
										role="alert"
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 13,
											color: "var(--color-dashboard-muted)",
											textAlign: "center",
											padding: "12px 0",
										}}
									>
										Couldn't load contractors.{" "}
										<StudioButton
											variant="ghost"
											size="sm"
											onClick={() =>
												hasSearched ? handleSearch() : loadDefaultContractors()
											}
										>
											Retry
										</StudioButton>
									</p>
								) : (searching || loadingDefault) &&
									searchResults.length === 0 ? (
									<Skeleton className="h-16 w-full rounded-xl" />
								) : searchResults.length === 0 ? (
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 13,
											color: "var(--color-dashboard-muted-light)",
											fontStyle: "italic",
											textAlign: "center",
											padding: "12px 0",
										}}
									>
										{hasSearched
											? `No verified contractors found for “${searchQuery}”`
											: "No verified contractors available to invite right now"}
									</p>
								) : (
									searchResults.map((c) => (
										<div
											key={c.id}
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												padding: "14px 16px",
												background: "var(--color-dashboard-bg)",
												border: "1px solid var(--color-dashboard-border)",
												borderRadius: 8,
											}}
										>
											<div>
												<p
													style={{
														fontFamily: "var(--font-display)",
														fontStyle: "italic",
														fontSize: 15,
														fontWeight: 500,
														color: "var(--color-dashboard-text)",
													}}
												>
													{c.name}
												</p>
												<p
													style={{
														fontFamily: "var(--font-mono)",
														fontSize: 9,
														letterSpacing: "0.1em",
														color: "var(--color-dashboard-muted)",
														textTransform: "uppercase",
														marginTop: 4,
													}}
												>
													{c.trades?.join(", ")} |{" "}
													{t("project.yExp", { years: c.experienceYears })}
												</p>
											</div>
											<StudioButton
												variant="primary"
												size="sm"
												onClick={() => handleInvite(c.id)}
												disabled={inviting === c.id}
											>
												{inviting === c.id ? "Inviting…" : t("project.invite")}
											</StudioButton>
										</div>
									))
								)}
							</div>
						)}

						<div className="mt-4">
							<StudioButton
								variant="ghost"
								size="sm"
								onClick={() => {
									setShowInvite(false);
									setSearchQuery("");
									setSearchResults([]);
									setSuggestions([]);
									setHasSearched(false);
								}}
							>
								{t("common.cancel")}
							</StudioButton>
						</div>
					</FloatPanel>
				)}

				{/* Contractor list */}
				{activeContractors.length === 0 ? (
					<FloatPanel elevation={2} padding={32} className="text-center mt-5">
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 13.5,
								color: "var(--color-dashboard-muted-light)",
								fontStyle: "italic",
							}}
						>
							{t("project.noContractorsYet")}
						</p>
						{project.status !== "in_progress" && (
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 12,
									color: "var(--color-dashboard-muted)",
									marginTop: 8,
								}}
							>
								{t("project.contractorsAddWhenInProgress")}
							</p>
						)}
					</FloatPanel>
				) : (
					<div className="mt-5 space-y-4">
						{activeContractors.map((item) => (
							<FloatPanel key={item.id} elevation={2} padding={22}>
								<div className="flex items-start justify-between">
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2">
											<p
												style={{
													fontFamily: "var(--font-display)",
													fontStyle: "italic",
													fontSize: 18,
													fontWeight: 500,
													color: "var(--color-dashboard-text)",
												}}
											>
												{item.contractor?.name || t("project.contractors")}
											</p>
											<StatusBadge status={item.status} />
										</div>
										{item.contractor?.trades && (
											<p
												style={{
													fontFamily: "var(--font-mono)",
													fontSize: 9,
													letterSpacing: "0.1em",
													color: "var(--color-dashboard-accent-dark)",
													textTransform: "uppercase",
													marginTop: 6,
												}}
											>
												{item.contractor.trades.join(", ")}
											</p>
										)}
										{item.quoteAmount != null && (
											<p
												style={{
													fontFamily: "var(--font-body)",
													fontSize: 13,
													color: "var(--color-dashboard-text)",
													marginTop: 8,
												}}
											>
												{t("common.quote")}{" "}
												<span
													style={{
														fontFamily: "var(--font-display)",
														fontWeight: 500,
													}}
												>
													{formatPrice(item.quoteAmount)}
												</span>
											</p>
										)}
										{item.quoteDetails && (
											<p
												style={{
													fontFamily: "var(--font-body)",
													fontSize: 12.5,
													color: "var(--color-dashboard-muted-alt)",
													marginTop: 6,
													lineHeight: 1.5,
												}}
											>
												{item.quoteDetails}
											</p>
										)}
										{item.message && (
											<p
												style={{
													fontFamily: "var(--font-body)",
													fontSize: 12.5,
													color: "var(--color-dashboard-muted-alt)",
													fontStyle: "italic",
													marginTop: 8,
												}}
											>
												{t("opportunities.contractorMessage")}: {item.message}
											</p>
										)}
										{item.hiredAt && (
											<p
												style={{
													fontFamily: "var(--font-mono)",
													fontSize: 9,
													letterSpacing: "0.1em",
													color: "var(--color-dashboard-muted)",
													textTransform: "uppercase",
													marginTop: 8,
												}}
											>
												{t("project.hired")} {formatDate(item.hiredAt)}
											</p>
										)}
									</div>
									<div className="flex shrink-0 gap-2">
										{(item.status === "quote_submitted" ||
											item.status === "interested") && (
											<StudioButton
												variant="primary"
												size="sm"
												onClick={() => handleHire(item.id)}
												disabled={actionLoading === item.id}
											>
												{actionLoading === item.id
													? "Hiring..."
													: t("project.hire")}
											</StudioButton>
										)}
										{item.status === "completion_requested" && (
											<StudioButton
												variant="primary"
												size="sm"
												onClick={() => handleConfirmCompletion(item.id)}
												disabled={actionLoading === item.id}
											>
												{actionLoading === item.id
													? "Confirming..."
													: t(
															"project.confirmCompletion",
															"Confirm Completion",
														)}
											</StudioButton>
										)}
										{item.status === "completed" &&
											(() => {
												const isReleased =
													releasedByPayments.has(item.id) ||
													authorizedIds.has(item.id);
												const disputeStatus = getDisputeStatus(
													item.completedAt,
													disputeWindowHours,
												);
												const designerPaid =
													item.paymentArrangement === "designer_paid";
												if (isReleased) {
													return (
														<span className="text-xs text-muted-foreground">
															Payment released
														</span>
													);
												}
												return (
													<div className="flex shrink-0 flex-col items-end gap-1">
														<div className="flex gap-2">
															<StudioButton
																variant="secondary"
																size="sm"
																onClick={() => handleDisputeCompletion(item.id)}
																disabled={
																	actionLoading === item.id ||
																	disputeStatus.isExpired
																}
															>
																{actionLoading === item.id
																	? "Disputing..."
																	: disputeStatus.label}
															</StudioButton>
															{/* A contractor the designer hired is paid by the
															    designer directly, and `authorizeAssignmentPayment`
															    refuses an escrow payout for them. */}
															{!designerPaid && (
																<StudioButton
																	variant="primary"
																	size="sm"
																	onClick={() =>
																		handleAuthorizePayment(item.id)
																	}
																	disabled={actionLoading === item.id}
																>
																	{actionLoading === item.id
																		? "Authorizing..."
																		: t(
																				"project.authorizePayment",
																				"Authorize Payment",
																			)}
																</StudioButton>
															)}
														</div>
														{disputeStatus.isExpired && (
															<span className="text-xs text-muted-foreground">
																Dispute window expired
															</span>
														)}
													</div>
												);
											})()}
										{item.status !== "completed" &&
											item.status !== "completion_requested" && (
												<StudioButton
													variant="ghost"
													size="sm"
													onClick={() => handleRemove(item.id)}
													disabled={actionLoading === item.id}
												>
													<Trash2 className="h-4 w-4 text-destructive" />
												</StudioButton>
											)}
									</div>
								</div>
							</FloatPanel>
						))}
					</div>
				)}
			</div>
		</FloatPanel>
	);
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export function ProjectDetailPage(): React.ReactElement {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { getUserType, user } = useAuthStore();
	const activeRole = getUserType();
	const userId = user?.id;
	const [project, setProject] = useState<Project | null>(null);
	// The user's side of this project picks its controls. Until the project
	// loads, the active role stands in.
	const userType = project?.viewerRole ?? activeRole;
	// Set when that side is not the active role: the page then shows only the
	// project's title and a switch to that role.
	const viewingAsRole =
		project?.viewerRole && project.viewerRole !== activeRole
			? project.viewerRole
			: null;

	const isSectionAllowed = useCallback(
		(section: SectionKey) => {
			if (userType === "contractor") {
				return CONTRACTOR_SECTIONS.includes(section);
			}
			return true;
		},
		[userType],
	);

	const visibleTabs = useMemo(() => {
		return TABS.filter((tab) => isSectionAllowed(tab.key));
	}, [isSectionAllowed]);

	const [tasks, setTasks] = useState<Task[]>([]);
	const [milestones, setMilestones] = useState<Milestone[]>([]);
	const [costs, setCosts] = useState<CostEstimate[]>([]);
	const [activities, setActivities] = useState<ActivityLog[]>([]);
	const [proposals, setProposals] = useState<ProjectProposalItem[]>([]);
	const [contractors, setContractors] = useState<ProjectContractorItem[]>([]);

	const [paymentTransactions, setPaymentTransactions] = useState<
		EscrowTransaction[]
	>([]);
	const [paymentSummary, setPaymentSummary] = useState<PaymentsSummary>({
		totalFunded: 0,
		totalHeld: 0,
		totalReleased: 0,
	});
	const [shortlist, setShortlist] = useState<ShortlistEntry[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [proposalsLoading, setProposalsLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const [activeTab, setActiveTab] = useState<TabKey>(() => {
		const tab = resolveTabKey(
			searchParams.get("tab"),
			DESIGNER_RETIRED_TAB_KEYS,
			DESIGNER_TAB_KEYS,
		);
		if (tab && isSectionAllowed(tab)) return tab;
		return "overview";
	});

	// Sync tab when URL query param changes (SPA navigation from notifications)
	useEffect(() => {
		const raw = searchParams.get("tab");
		const tab = resolveTabKey(
			raw,
			DESIGNER_RETIRED_TAB_KEYS,
			DESIGNER_TAB_KEYS,
		);
		if (!tab || !isSectionAllowed(tab)) return;
		setActiveTab(tab);
		if (shouldRewriteTabKey(raw, tab, HOMEOWNER_TAB_IDS)) {
			// The tab bar writes the active key to the URL on every click, so a
			// link carrying a retired key is rewritten the same way and a copied
			// URL carries the current key. A key the homeowner surface still has
			// as a live tab is left alone instead: `milestones` opens Plan here
			// and Milestones there, and only the raw key opens both.
			// replace, not push, so Back leaves the page rather than returning
			// to the retired key.
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.set("tab", tab);
					return next;
				},
				{ replace: true },
			);
		}
	}, [searchParams, isSectionAllowed, setSearchParams]);

	const [fundingLoading, setFundingLoading] = useState(false);
	const [selectedMilestoneIds, setSelectedMilestoneIds] = useState<string[]>(
		[],
	);

	// Payout account nudge: designers must have a synced account before any
	// milestone release can pay out. The account is user-scoped (/me), so it is
	// only refetched when the fundable-status check starts applying.
	const [payoutAccount, setPayoutAccount] = useState<PayoutAccount | null>(
		null,
	);
	const [payoutChecked, setPayoutChecked] = useState(false);
	const projectStatus = project?.status;

	useEffect(() => {
		if (userType !== "designer" || viewingAsRole) return;
		if (projectStatus !== "in_progress" && projectStatus !== "awaiting_funding")
			return;
		let cancelled = false;
		getMyPayoutAccount().then((account) => {
			if (cancelled) return;
			setPayoutAccount(account);
			setPayoutChecked(true);
		});
		return () => {
			cancelled = true;
		};
	}, [userType, viewingAsRole, projectStatus]);
	// Which nudge copy applies: no account yet, or one added but not synced.
	const payoutNudgeVariant = payoutAccount ? "Unsynced" : "";
	const [fundingStatusMessage, setFundingStatusMessage] = useState<
		string | null
	>(null);
	const [proposalActionError, setProposalActionError] =
		useState<ProposalActionError | null>(null);
	const [proposalActionLoadingId, setProposalActionLoadingId] = useState<
		string | null
	>(null);
	const [releaseLoading, setReleaseLoading] = useState<string | null>(null);
	const [releaseErrors, setReleaseErrors] = useState<Record<string, string>>(
		{},
	);

	// Public toggle
	const [isTogglingPublic, setIsTogglingPublic] = useState(false);
	const [isSavingProjectImages, setIsSavingProjectImages] = useState(false);

	// Cancel dialog
	const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
	const [cancelReason, setCancelReason] = useState("");

	// Draft edit
	const [isEditing, setIsEditing] = useState(false);
	const [editTitle, setEditTitle] = useState("");
	const [editScope, setEditScope] = useState<"full_home" | "partial">(
		"full_home",
	);
	const [editBudgetMin, setEditBudgetMin] = useState("");
	const [editBudgetMax, setEditBudgetMax] = useState("");
	const [editTimelineWeeks, setEditTimelineWeeks] = useState("");
	const [editSaving, setEditSaving] = useState(false);

	// --------------------------------------------------------------------------
	// Data Loading
	// --------------------------------------------------------------------------

	const loadProject = useCallback(async () => {
		if (!id) return;
		setIsLoading(true);
		try {
			const projectData = await getProjectById(id);
			setProject(projectData);
		} finally {
			setIsLoading(false);
		}
	}, [id]);

	// After a visibility change that did not go through, the switch shows what
	// the server holds now (another call may have flipped it first). A failed
	// read leaves the page as it is.
	const showCurrentProject = useCallback(async (projectId: string) => {
		try {
			const projectData = await getProjectById(projectId);
			if (projectData) setProject(projectData);
		} catch {
			// the toast already reported the failed toggle
		}
	}, []);

	const refreshOverviewData = useCallback(async () => {
		if (!id) return;

		const projectData = await getProjectById(id);
		if (!projectData) return;
		setProject(projectData);

		if (userType === "homeowner") {
			setProposalsLoading(true);
			try {
				const proposalData = await getProjectProposals(id);
				setProposals(proposalData);
			} finally {
				setProposalsLoading(false);
			}
		}

		if (projectData.status === "awaiting_funding") {
			const milestoneData = await getProjectMilestones(id);
			setMilestones(milestoneData);
		}
	}, [id, userType]);

	const loadTabData = useCallback(async () => {
		if (!id) return;

		switch (activeTab) {
			case "overview": {
				const loadProposals = userType === "homeowner";
				// Milestones feed the funding banner.
				const loadMilestones = project?.status === "awaiting_funding";
				// The activity feed is a panel of the overview body, for the
				// readers the activity section admits.
				const loadActivity = isSectionAllowed("activity");

				setProposalsLoading(loadProposals);

				// None of these three reads another's result, and each helper
				// catches its own request failure and resolves with an empty
				// list instead of rejecting, so a failed request cannot discard
				// a sibling's result here.
				try {
					const [proposalData, milestoneData, activityData] = await Promise.all(
						[
							loadProposals ? getProjectProposals(id) : null,
							loadMilestones ? getProjectMilestones(id) : null,
							loadActivity ? getProjectActivity(id) : null,
						],
					);

					if (proposalData) setProposals(proposalData);
					if (milestoneData) setMilestones(milestoneData);
					if (activityData) setActivities(activityData);
				} finally {
					setProposalsLoading(false);
				}
				break;
			}
			case "team": {
				const contractorData = await getProjectContractors(id);
				setContractors(contractorData.contractors);
				break;
			}
			case "plan": {
				const [taskData, milestoneData] = await Promise.all([
					getProjectTasks(id),
					getProjectMilestones(id),
				]);
				setTasks(taskData);
				setMilestones(milestoneData);
				break;
			}
			// The BOQ body renders the cost estimates above the BOQ list, so the
			// costs fetch belongs to this tab. DesignerBoqTab fetches the BOQs
			// themselves through useProjectBOQs.
			case "boq": {
				// The backend refuses a contractor the cost estimates, and the tab
				// does not show them one.
				if (userType === "contractor") break;
				const costData = await getProjectCosts(id);
				setCosts(costData);
				break;
			}
			case "payments": {
				const paymentData = await getProjectPayments(id);
				setPaymentTransactions(paymentData.transactions);
				setPaymentSummary(paymentData.summary);
				break;
			}
			case "materials": {
				const data = await getProjectShortlist(id);
				setShortlist(data);
				break;
			}
		}
	}, [id, activeTab, userType, isSectionAllowed, project?.status]);

	useEffect(() => {
		if (id) {
			loadProject();
		}
	}, [id, loadProject]);

	useEffect(() => {
		if (id && !isLoading && project && !viewingAsRole) {
			loadTabData();
		}
	}, [id, isLoading, project, viewingAsRole, loadTabData]);

	const refreshTasks = async () => {
		if (!id) return;
		const taskData = await getProjectTasks(id);
		setTasks(taskData);
	};

	const refreshMilestones = async () => {
		if (!id) return;
		const milestoneData = await getProjectMilestones(id);
		setMilestones(milestoneData);
	};

	const refreshCosts = async () => {
		if (!id) return;
		const costData = await getProjectCosts(id);
		setCosts(costData);
	};

	const refreshContractors = async () => {
		if (!id) return;
		const contractorData = await getProjectContractors(id);
		setContractors(contractorData.contractors);
	};

	// --------------------------------------------------------------------------
	// Payment Actions
	// --------------------------------------------------------------------------

	const handleFundProject = async () => {
		if (!id) return;
		setFundingLoading(true);
		setFundingStatusMessage(t("project.payments.checkoutStarting"));

		let fundingVerified = false;
		try {
			// Pass selected milestone IDs if user selected specific ones
			const result = await fundProject(
				id,
				selectedMilestoneIds.length > 0 ? selectedMilestoneIds : undefined,
			);
			if (!result.success || !result.fundingSession) {
				setFundingStatusMessage(null);
				toast.error(result.error || "Failed to fund project");
				return;
			}

			const { fundingSession } = result;

			if (fundingSession.provider === "cashfree") {
				if (!fundingSession.paymentSessionId) {
					setFundingStatusMessage(null);
					toast.error(t("project.payments.checkoutUnavailable"));
					return;
				}

				setFundingStatusMessage(t("project.payments.checkoutOpen"));
				const checkoutResult = await launchCashfreeCheckout(
					fundingSession.paymentSessionId,
				);

				if (checkoutResult.status === "cancelled") {
					setFundingStatusMessage(t("project.payments.paymentNotCompleted"));
					toast.error(t("project.payments.paymentNotCompleted"));
					return;
				}
			}

			setFundingStatusMessage(t("project.payments.verifyingFunding"));
			const verification = await verifyProjectFunding(
				id,
				fundingSession.providerOrderId,
			);

			if (verification.success && verification.verified) {
				fundingVerified = true;
				setFundingStatusMessage(null);
				toast.success(t("project.payments.statusFunded"));
				await loadProject();
			} else if (verification.success) {
				setFundingStatusMessage(
					t("project.payments.paymentPendingConfirmation"),
				);
				toast.error(t("project.payments.paymentPendingConfirmation"));
			} else {
				setFundingStatusMessage(t("project.payments.verificationFailed"));
				toast.error(
					verification.error || t("project.payments.verificationFailed"),
				);
			}
		} catch {
			// Past `fundingVerified = true` the server has already confirmed the
			// payment; all that follows is the success feedback and loadProject. The
			// checkout-failed message would be false, and the Fund button is still on
			// screen until a reload clears the banner, so it reads as "pay again".
			if (fundingVerified) {
				toast.error(t("project.payments.fundingRefreshFailed"));
				return;
			}
			setFundingStatusMessage(t("project.payments.checkoutFailed"));
			toast.error(t("project.payments.checkoutFailed"));
		} finally {
			setFundingLoading(false);
		}
	};

	const handleAcceptProposal = async (proposalId: string) => {
		setProposalActionLoadingId(proposalId);
		setProposalActionError(null);

		let accepted = false;
		try {
			const result = await acceptProposal(proposalId);
			if (!result.success) {
				setProposalActionError({
					proposalId,
					message: result.error || t("proposals.failedToSubmit"),
				});
				return;
			}
			accepted = true;

			void queryClient.invalidateQueries({
				queryKey: queryKeys.projects.lists(),
			});
			toast.success(t("proposals.proposalAcceptedStatus"));
			await refreshOverviewData();
		} catch {
			// Past `accepted = true` the server has already accepted the proposal, so
			// anything that throws after it is the refresh, not the accept. Report
			// that transiently instead of as a per-proposal accept failure.
			if (accepted) {
				toast.error(t("proposals.refreshFailed"));
				return;
			}
			setProposalActionError({
				proposalId,
				message: t("proposals.unexpectedError"),
			});
		} finally {
			setProposalActionLoadingId(null);
		}
	};

	const handleRejectProposal = async (proposalId: string) => {
		setProposalActionLoadingId(proposalId);
		setProposalActionError(null);

		let rejected = false;
		try {
			const result = await rejectProposal(proposalId);
			if (!result.success) {
				setProposalActionError({
					proposalId,
					message: result.error || t("proposals.failedToSubmit"),
				});
				return;
			}
			rejected = true;

			toast.success(t("proposals.proposalRejectedStatus"));
			await refreshOverviewData();
		} catch {
			// Past `rejected = true` the server has already rejected the proposal, so
			// anything that throws after it is the refresh, not the reject. Report
			// that transiently instead of as a per-proposal reject failure.
			if (rejected) {
				toast.error(t("proposals.refreshFailed"));
				return;
			}
			setProposalActionError({
				proposalId,
				message: t("proposals.unexpectedError"),
			});
		} finally {
			setProposalActionLoadingId(null);
		}
	};

	const handleReleaseMilestonePayment = async (milestoneId: string) => {
		setReleaseLoading(milestoneId);
		setReleaseErrors((current) => {
			if (!current[milestoneId]) return current;
			const next = { ...current };
			delete next[milestoneId];
			return next;
		});
		try {
			const result = await releaseMilestonePayment(milestoneId);
			if (result.success) {
				toast.success(t("project.payments.statusReleased"));
				setReleaseErrors((current) => {
					if (!current[milestoneId]) return current;
					const next = { ...current };
					delete next[milestoneId];
					return next;
				});
				await refreshMilestones();
			} else {
				const errorMessage =
					result.error || t("project.payments.releasePaymentFailed");
				setReleaseErrors((current) => ({
					...current,
					[milestoneId]: errorMessage,
				}));
				toast.error(errorMessage);
			}
		} finally {
			setReleaseLoading(null);
		}
	};

	// --------------------------------------------------------------------------
	// Actions
	// --------------------------------------------------------------------------

	const handleComplete = async () => {
		if (!id) return;
		setActionLoading(true);
		try {
			const result = await completeProject(id);
			if (result.success) {
				await loadProject();
			}
		} finally {
			setActionLoading(false);
		}
	};

	const handleCancel = async () => {
		if (!id) return;
		setActionLoading(true);
		try {
			const result = await cancelProject(id, cancelReason.trim() || undefined);
			if (result.success) {
				setCancelDialogOpen(false);
				setCancelReason("");
				await loadProject();
			}
		} finally {
			setActionLoading(false);
		}
	};

	const startEditing = () => {
		if (!project) return;
		setEditTitle(project.title);
		setEditScope(project.scope);
		setEditBudgetMin(
			project.budgetMin != null ? String(project.budgetMin) : "",
		);
		setEditBudgetMax(
			project.budgetMax != null ? String(project.budgetMax) : "",
		);
		setEditTimelineWeeks(
			project.timelineWeeks != null ? String(project.timelineWeeks) : "",
		);
		setIsEditing(true);
	};

	const handleSaveEdit = async () => {
		if (!id || !editTitle.trim()) return;
		setEditSaving(true);
		try {
			const result = await updateProject(id, {
				title: editTitle.trim(),
				scope: editScope,
				budgetMin: editBudgetMin ? parseInt(editBudgetMin, 10) : undefined,
				budgetMax: editBudgetMax ? parseInt(editBudgetMax, 10) : undefined,
				timelineWeeks: editTimelineWeeks
					? parseInt(editTimelineWeeks, 10)
					: undefined,
			});
			if (result.success) {
				setIsEditing(false);
				await loadProject();
			}
		} finally {
			setEditSaving(false);
		}
	};

	const handleUpdateProjectImages = async (data: {
		beforeImages: string[];
		afterImages: string[];
	}) => {
		if (!id) return;
		setIsSavingProjectImages(true);
		try {
			const result = await updateProjectTransformationImages(id, data);
			if (result.success && result.project) {
				setProject(result.project);
				toast.success("Transformation images updated");
			} else {
				toast.error(result.error || t("common.somethingWentWrong"));
			}
		} catch {
			toast.error(t("common.somethingWentWrong"));
		} finally {
			setIsSavingProjectImages(false);
		}
	};

	const handleAddBeforeImages = (newUrls: string[]) => {
		if (!project) return;
		void handleUpdateProjectImages({
			beforeImages: [...(project.beforeImages || []), ...newUrls],
			afterImages: project.afterImages || [],
		});
	};

	const handleRemoveBeforeImage = (index: number) => {
		if (!project) return;
		void handleUpdateProjectImages({
			beforeImages: (project.beforeImages || []).filter((_, i) => i !== index),
			afterImages: project.afterImages || [],
		});
	};

	const handleAddAfterImages = (newUrls: string[]) => {
		if (!project) return;
		void handleUpdateProjectImages({
			beforeImages: project.beforeImages || [],
			afterImages: [...(project.afterImages || []), ...newUrls],
		});
	};

	const handleRemoveAfterImage = (index: number) => {
		if (!project) return;
		void handleUpdateProjectImages({
			beforeImages: project.beforeImages || [],
			afterImages: (project.afterImages || []).filter((_, i) => i !== index),
		});
	};

	// --------------------------------------------------------------------------
	// Render
	// --------------------------------------------------------------------------

	if (isLoading) {
		return <ProjectDetailSkeleton />;
	}

	if (!project) {
		return (
			<div
				className="min-h-screen py-8"
				style={{ background: "var(--color-dashboard-bg)" }}
			>
				<div className="mx-auto max-w-4xl px-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate("/projects")}
						className="mb-6"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						<ArrowLeft className="h-4 w-4" />
						{t("common.back")}
					</Button>
					<div
						className="p-8 text-center"
						style={{
							background: "var(--color-dashboard-warm-450)",
							border: "1px solid var(--color-dashboard-border)",
							borderRadius: 11,
						}}
					>
						<XCircle
							className="mx-auto h-12 w-12"
							style={{ color: "var(--color-dashboard-muted)" }}
						/>
						<h2
							className="mt-4 text-xl font-semibold"
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								color: "var(--color-dashboard-text)",
							}}
						>
							{t("project.projectNotFound")}
						</h2>
						<p
							className="mt-2"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{t("project.projectNotFoundDescAccess")}
						</p>
						<Button
							variant="primary"
							onClick={() =>
								navigate(
									userType === "homeowner"
										? "/home"
										: userType === "contractor"
											? "/assignments"
											: "/dashboard",
								)
							}
							className="mt-6"
							style={{
								background: "var(--color-dashboard-accent)",
								color: "var(--color-dashboard-warm-450)",
							}}
						>
							{t("common.goToDashboard")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	if (viewingAsRole) {
		return (
			<ProjectViewingAs
				title={project.title}
				status={project.status}
				viewerRole={viewingAsRole}
				backLabel={
					activeRole === "contractor"
						? t("common.backToAssignments", "Back to Assignments")
						: t("common.backToProjects", "Back to Projects")
				}
				onBack={() =>
					navigate(activeRole === "contractor" ? "/assignments" : "/projects")
				}
			/>
		);
	}

	const isTerminal =
		project.status === "completed" || project.status === "cancelled";

	return (
		<div
			className="min-h-screen py-8"
			style={{ background: "var(--color-dashboard-bg)" }}
		>
			<div className="mx-auto max-w-4xl px-4">
				{/* Back Button */}
				<Button
					variant="ghost"
					size="sm"
					onClick={() =>
						navigate(
							userType === "homeowner"
								? "/home"
								: userType === "contractor"
									? "/assignments"
									: "/projects",
						)
					}
					className="mb-6"
					style={{ color: "var(--color-dashboard-muted)" }}
				>
					<ArrowLeft className="h-4 w-4" />
					{userType === "contractor"
						? t("common.backToAssignments", "Back to Assignments")
						: t("common.backToProjects", "Back to Projects")}
				</Button>

				{/* Header */}
				<div className="mb-6">
					<div className="flex flex-wrap items-start justify-between gap-4">
						<div className="min-w-0 flex-1">
							<h1
								className="text-2xl"
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
									letterSpacing: "-0.01em",
								}}
							>
								{project.title}
							</h1>
							<div className="mt-2 flex flex-wrap items-center gap-3">
								<StatusBadge status={project.status} />
								{project.budgetMin != null && project.budgetMax != null && (
									<span
										className="flex items-center gap-1 text-sm"
										style={{ color: "var(--color-dashboard-muted)" }}
									>
										<IndianRupee className="h-3.5 w-3.5" />
										{formatPrice(project.budgetMin)} -{" "}
										{formatPrice(project.budgetMax)}
									</span>
								)}
								{project.timelineWeeks && (
									<span
										className="flex items-center gap-1 text-sm"
										style={{ color: "var(--color-dashboard-muted)" }}
									>
										<Clock className="h-3.5 w-3.5" />
										{project.timelineWeeks} {t("common.weeks")}
									</span>
								)}
								<span
									className="flex items-center gap-1 text-sm"
									style={{ color: "var(--color-dashboard-muted)" }}
								>
									<ClipboardList className="h-3.5 w-3.5" />
									{project.scope === "full_home"
										? t("common.fullHome")
										: t("common.partial")}
								</span>
							</div>
							{/* Public Toggle */}
							{userType === "homeowner" && (
								<div className="mt-3 flex items-center gap-2">
									<Switch
										id="public-toggle"
										checked={project.isPublic ?? false}
										disabled={
											isTogglingPublic || project.status !== "in_progress"
										}
										onCheckedChange={async () => {
											if (!id) return;
											if (project.status !== "in_progress") return;
											setIsTogglingPublic(true);
											try {
												const result = await toggleProjectPublic(id);
												if (result.success && result.project) {
													setProject(result.project);
													toast.success(
														result.project.isPublic
															? t("opportunities.publicProject")
															: t("opportunities.privateProject"),
													);
												} else {
													toast.error(
														result.error || t("common.somethingWentWrong"),
													);
													await showCurrentProject(id);
												}
											} catch {
												toast.error(t("common.somethingWentWrong"));
												await showCurrentProject(id);
											} finally {
												setIsTogglingPublic(false);
											}
										}}
									/>
									<label
										htmlFor="public-toggle"
										className="cursor-pointer text-sm"
										style={{ color: "var(--color-dashboard-muted)" }}
									>
										{project.isPublic
											? t("opportunities.publicProject")
											: t("opportunities.privateProject")}
									</label>
									{project.status !== "in_progress" && (
										<span
											className="text-xs"
											style={{ color: "var(--color-dashboard-muted)" }}
										>
											Can be published once project is in progress
										</span>
									)}
								</div>
							)}
						</div>

						{/* Status Actions */}
						{userType !== "contractor" && (
							<div className="flex gap-2">
								{project.status === "draft" && (
									<Button
										size="sm"
										onClick={startEditing}
										style={{
											borderColor: "var(--color-dashboard-border)",
											color: "var(--color-dashboard-text)",
										}}
									>
										{t("common.edit")}
									</Button>
								)}
								{project.status === "in_progress" && (
									<Button
										size="sm"
										onClick={handleComplete}
										isLoading={actionLoading}
										style={{
											background: "var(--color-dashboard-accent)",
											color: "var(--color-dashboard-warm-450)",
										}}
									>
										<CheckCircle2 className="h-4 w-4" />
										{t("project.markComplete")}
									</Button>
								)}
								{!isTerminal && (
									<AlertDialog
										open={cancelDialogOpen}
										onOpenChange={setCancelDialogOpen}
									>
										<AlertDialogTrigger asChild>
											<Button
												size="sm"
												style={{
													borderColor: "var(--color-dashboard-border)",
													color: "var(--color-dashboard-text)",
												}}
											>
												<XCircle className="h-4 w-4" />
												{t("project.cancelProject")}
											</Button>
										</AlertDialogTrigger>
										<AlertDialogContent
											style={{
												background: "var(--color-dashboard-warm-450)",
												border: "1px solid var(--color-dashboard-border)",
											}}
										>
											<AlertDialogHeader>
												<AlertDialogTitle
													style={{
														fontFamily: "var(--font-display)",
														fontStyle: "italic",
														color: "var(--color-dashboard-text)",
													}}
												>
													{t("project.cancelProjectConfirm")}
												</AlertDialogTitle>
												<AlertDialogDescription
													style={{ color: "var(--color-dashboard-muted)" }}
												>
													{t("project.cancelProjectWarning")}
												</AlertDialogDescription>
											</AlertDialogHeader>
											<div className="my-4">
												<Label
													htmlFor="cancel-reason"
													className="mb-1 block text-sm"
													style={{ color: "var(--color-dashboard-text)" }}
												>
													{t("project.reasonOptional")}
												</Label>
												<Textarea
													id="cancel-reason"
													value={cancelReason}
													onChange={(e) => setCancelReason(e.target.value)}
													placeholder={t("project.cancelReasonPlaceholder")}
													rows={3}
													style={{
														borderColor: "var(--color-dashboard-border)",
														background: "var(--color-dashboard-warm-450)",
														color: "var(--color-dashboard-text)",
													}}
												/>
											</div>
											<AlertDialogFooter>
												<AlertDialogCancel
													style={{
														borderColor: "var(--color-dashboard-border)",
														color: "var(--color-dashboard-muted)",
													}}
												>
													{t("project.goBack")}
												</AlertDialogCancel>
												<AlertDialogAction
													onClick={handleCancel}
													style={{
														background: "var(--color-dashboard-accent)",
														color: "var(--color-dashboard-warm-450)",
													}}
												>
													{actionLoading
														? t("project.cancelling")
														: t("project.yesCancelProject")}
												</AlertDialogAction>
											</AlertDialogFooter>
										</AlertDialogContent>
									</AlertDialog>
								)}
							</div>
						)}
					</div>
				</div>

				{/* Draft Edit Form */}
				{isEditing && project.status === "draft" && (
					<div
						className="mb-6"
						style={{
							background: "var(--color-dashboard-warm-450)",
							border: "1px solid var(--color-dashboard-border)",
							borderRadius: 11,
							padding: 24,
						}}
					>
						<div className="mb-4">
							<h3
								className="text-lg"
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									color: "var(--color-dashboard-text)",
								}}
							>
								{t("project.editProject")}
							</h3>
							<p
								className="text-sm"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								{t("project.updateDraftDetails")}
							</p>
						</div>
						<div className="space-y-4">
							<div>
								<Label
									className="mb-1 block"
									style={{ color: "var(--color-dashboard-text)" }}
								>
									{t("project.title")}
								</Label>
								<Input
									value={editTitle}
									onChange={(e) => setEditTitle(e.target.value)}
									placeholder={t("project.projectTitlePlaceholder")}
									style={{
										borderColor: "var(--color-dashboard-border)",
										background: "var(--color-dashboard-warm-450)",
										color: "var(--color-dashboard-text)",
									}}
								/>
							</div>
							<div>
								<Label
									className="mb-1 block"
									style={{ color: "var(--color-dashboard-text)" }}
								>
									{t("project.scope")}
								</Label>
								<Select
									value={editScope}
									onValueChange={(val) =>
										setEditScope(val as "full_home" | "partial")
									}
								>
									<SelectTrigger
										className="w-full"
										style={{ borderColor: "var(--color-dashboard-border)" }}
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="full_home">
											{t("common.fullHome")}
										</SelectItem>
										<SelectItem value="partial">
											{t("common.partial")}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label
									className="mb-1 block"
									style={{ color: "var(--color-dashboard-text)" }}
								>
									{t("project.budgetRangeInr")}
								</Label>
								<div className="grid grid-cols-2 gap-4">
									<Input
										type="number"
										value={editBudgetMin}
										onChange={(e) => setEditBudgetMin(e.target.value)}
										placeholder={t("project.minPlaceholder")}
										min={0}
										style={{
											borderColor: "var(--color-dashboard-border)",
											background: "var(--color-dashboard-warm-450)",
											color: "var(--color-dashboard-text)",
										}}
									/>
									<Input
										type="number"
										value={editBudgetMax}
										onChange={(e) => setEditBudgetMax(e.target.value)}
										placeholder={t("project.maxPlaceholder")}
										min={0}
										style={{
											borderColor: "var(--color-dashboard-border)",
											background: "var(--color-dashboard-warm-450)",
											color: "var(--color-dashboard-text)",
										}}
									/>
								</div>
							</div>
							<div>
								<Label
									className="mb-1 block"
									style={{ color: "var(--color-dashboard-text)" }}
								>
									{t("project.timelineWeeks")}
								</Label>
								<Input
									type="number"
									value={editTimelineWeeks}
									onChange={(e) => setEditTimelineWeeks(e.target.value)}
									placeholder={t("project.timelineWeeksPlaceholder")}
									min={1}
									style={{
										borderColor: "var(--color-dashboard-border)",
										background: "var(--color-dashboard-warm-450)",
										color: "var(--color-dashboard-text)",
									}}
								/>
							</div>
						</div>
						<div className="mt-6 flex gap-2">
							<Button
								size="sm"
								onClick={handleSaveEdit}
								isLoading={editSaving}
								style={{
									background: "var(--color-dashboard-accent)",
									color: "var(--color-dashboard-warm-450)",
								}}
							>
								{t("common.saveChanges")}
							</Button>
							<Button
								size="sm"
								onClick={() => setIsEditing(false)}
								style={{
									borderColor: "var(--color-dashboard-border)",
									color: "var(--color-dashboard-muted)",
								}}
							>
								{t("common.cancel")}
							</Button>
						</div>
					</div>
				)}

				{/* Payout account nudge for designers */}
				{userType === "designer" &&
					payoutChecked &&
					(!payoutAccount || payoutAccount.syncStatus !== "synced") &&
					project &&
					(project.status === "in_progress" ||
						project.status === "awaiting_funding") && (
						<div
							className="mb-4"
							style={{
								background: "var(--color-dashboard-warm-100)",
								border: "1px solid var(--color-dashboard-border)",
								borderRadius: 11,
								padding: 20,
							}}
						>
							<div className="flex items-start justify-between gap-4">
								<div className="flex-1">
									<p
										className="font-medium"
										style={{ color: "var(--color-dashboard-accent-dark)" }}
									>
										{t(
											`project.payments.payoutNudge${payoutNudgeVariant}Title`,
										)}
									</p>
									<p
										className="text-sm"
										style={{ color: "var(--color-dashboard-muted)" }}
									>
										{t(`project.payments.payoutNudge${payoutNudgeVariant}Desc`)}
									</p>
								</div>
								<Button onClick={() => navigate("/settings#payout")}>
									{t(`project.payments.payoutNudge${payoutNudgeVariant}Cta`)}
								</Button>
							</div>
						</div>
					)}

				{/* Tab Navigation */}
				<div
					className="mb-6 border-b"
					style={{ borderColor: "var(--color-dashboard-border)" }}
				>
					<nav className="-mb-px flex gap-1 overflow-x-auto">
						{visibleTabs.map((tab) => (
							<button
								type="button"
								key={tab.key}
								onClick={() => {
									setActiveTab(tab.key);
									setSearchParams((prev) => {
										const next = new URLSearchParams(prev);
										next.set("tab", tab.key);
										return next;
									});
								}}
								className={cn(
									"whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors",
									activeTab === tab.key ? "border-b-2" : "border-transparent",
								)}
								style={
									activeTab === tab.key
										? {
												borderColor: "var(--color-dashboard-accent)",
												color: "var(--color-dashboard-accent-dark)",
											}
										: { color: "var(--color-dashboard-muted)" }
								}
							>
								{t(tab.labelKey)}
							</button>
						))}
					</nav>
				</div>

				{/* Funding Banner */}
				{(project.status === "awaiting_funding" ||
					project.fundingStatus === "partially_funded") &&
					userType === "homeowner" && (
						<div
							className="mb-4"
							style={{
								background: "var(--color-dashboard-warm-100)",
								border: "1px solid var(--color-dashboard-border)",
								borderRadius: 11,
								padding: 24,
							}}
						>
							<div className="flex items-start justify-between gap-4">
								<div className="flex-1">
									<p
										className="font-medium"
										style={{ color: "var(--color-dashboard-accent-dark)" }}
									>
										{project.fundingStatus === "partially_funded"
											? t("project.payments.partiallyFunded")
											: t("project.payments.awaitingFunding")}
									</p>
									<p
										className="text-sm"
										style={{ color: "var(--color-dashboard-muted)" }}
									>
										{project.fundingStatus === "partially_funded"
											? t("project.payments.partiallyFundedDesc")
											: t("project.payments.awaitingFundingDesc")}
									</p>
									{fundingStatusMessage && (
										<p
											className="mt-1 text-sm"
											style={{ color: "var(--color-dashboard-accent-dark)" }}
										>
											{fundingStatusMessage}
										</p>
									)}

									{/* Milestone Selection */}
									{project.status === "awaiting_funding" &&
										milestones.length > 0 && (
											<div className="mt-3 space-y-2">
												<div className="flex items-center gap-2 text-sm">
													<button
														type="button"
														onClick={() => {
															const unfunded = milestones
																.filter((m) => m.fundingStatus === "unfunded")
																.map((m) => m.id);
															setSelectedMilestoneIds(unfunded);
														}}
														className="text-xs underline"
														style={{ color: "var(--color-dashboard-muted)" }}
													>
														{t("common.selectAll")}
													</button>
													<span
														style={{ color: "var(--color-dashboard-border)" }}
													>
														|
													</span>
													<button
														type="button"
														onClick={() => setSelectedMilestoneIds([])}
														className="text-xs underline"
														style={{ color: "var(--color-dashboard-muted)" }}
													>
														{t("common.deselectAll")}
													</button>
												</div>
												<div className="max-h-40 space-y-1 overflow-y-auto">
													{milestones
														.filter((m) => m.fundingStatus === "unfunded")
														.map((milestone) => (
															<label
																key={milestone.id}
																className="flex cursor-pointer items-center gap-2 rounded p-1"
																style={{
																	background: "var(--color-dashboard-warm-450)",
																}}
															>
																<input
																	type="checkbox"
																	checked={selectedMilestoneIds.includes(
																		milestone.id,
																	)}
																	onChange={(e) => {
																		if (e.target.checked) {
																			setSelectedMilestoneIds([
																				...selectedMilestoneIds,
																				milestone.id,
																			]);
																		} else {
																			setSelectedMilestoneIds(
																				selectedMilestoneIds.filter(
																					(id) => id !== milestone.id,
																				),
																			);
																		}
																	}}
																	className="h-4 w-4 rounded"
																	style={{
																		borderColor:
																			"var(--color-dashboard-border)",
																	}}
																/>
																<span
																	className="flex-1 text-sm"
																	style={{
																		color: "var(--color-dashboard-text)",
																	}}
																>
																	{milestone.title}
																</span>
																<span
																	className="text-sm font-medium"
																	style={{
																		color: "var(--color-dashboard-text)",
																	}}
																>
																	{milestone.paymentAmount
																		? formatPrice(milestone.paymentAmount)
																		: t("common.noCost")}
																</span>
															</label>
														))}
												</div>
												{selectedMilestoneIds.length > 0 && (
													<p
														className="text-sm font-medium"
														style={{
															color: "var(--color-dashboard-accent-dark)",
														}}
													>
														{t("project.payments.selected")}:{" "}
														{formatPrice(
															milestones
																.filter((m) =>
																	selectedMilestoneIds.includes(m.id),
																)
																.reduce(
																	(sum, m) => sum + (m.paymentAmount || 0),
																	0,
																),
														)}
													</p>
												)}
											</div>
										)}

									{/* Show total for already funded */}
									{milestones.length > 0 && (
										<p
											className="mt-2 text-sm font-medium"
											style={{ color: "var(--color-dashboard-accent-dark)" }}
										>
											{t("project.payments.totalFunded")}:{" "}
											{formatPrice(
												milestones.reduce(
													(sum, m) => sum + (m.paymentAmount || 0),
													0,
												),
											)}
										</p>
									)}
								</div>
								<AlertDialog>
									<AlertDialogTrigger asChild>
										<Button
											disabled={
												fundingLoading ||
												(project.status === "awaiting_funding" &&
													selectedMilestoneIds.length === 0)
											}
											style={{
												background: "var(--color-dashboard-accent)",
												color: "var(--color-dashboard-warm-450)",
											}}
										>
											{fundingLoading
												? t("project.payments.funding")
												: project.status === "awaiting_funding" &&
														selectedMilestoneIds.length > 0
													? t("project.payments.fundSelected")
													: t("project.payments.fundProject")}
										</Button>
									</AlertDialogTrigger>
									<AlertDialogContent
										style={{
											background: "var(--color-dashboard-warm-450)",
											border: "1px solid var(--color-dashboard-border)",
										}}
									>
										<AlertDialogHeader>
											<AlertDialogTitle
												style={{
													fontFamily: "var(--font-display)",
													fontStyle: "italic",
													color: "var(--color-dashboard-text)",
												}}
											>
												{t("project.payments.fundProjectConfirm")}
											</AlertDialogTitle>
											<AlertDialogDescription
												style={{ color: "var(--color-dashboard-muted)" }}
											>
												{project.status === "awaiting_funding" &&
												selectedMilestoneIds.length > 0
													? t("project.payments.fundSelectedConfirmMsg")
													: t("project.payments.fundProjectConfirmMsg")}
											</AlertDialogDescription>
										</AlertDialogHeader>
										<AlertDialogFooter>
											<AlertDialogCancel
												style={{
													borderColor: "var(--color-dashboard-border)",
													color: "var(--color-dashboard-muted)",
												}}
											>
												{t("common.cancel")}
											</AlertDialogCancel>
											<AlertDialogAction
												onClick={handleFundProject}
												style={{
													background: "var(--color-dashboard-accent)",
													color: "var(--color-dashboard-warm-450)",
												}}
											>
												{project.status === "awaiting_funding" &&
												selectedMilestoneIds.length > 0
													? t("project.payments.fundSelected")
													: t("project.payments.fundProject")}
											</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>
							</div>
						</div>
					)}

				{/* Tab Content */}
				{activeTab === "overview" && isSectionAllowed("overview") && (
					<div className="space-y-8">
						<OverviewTab
							project={project}
							proposals={proposals}
							proposalsLoading={proposalsLoading}
							proposalActionError={proposalActionError}
							proposalActionLoadingId={proposalActionLoadingId}
							userType={userType}
							isSavingProjectImages={isSavingProjectImages}
							onAddBeforeImages={handleAddBeforeImages}
							onRemoveBeforeImage={handleRemoveBeforeImage}
							onAddAfterImages={handleAddAfterImages}
							onRemoveAfterImage={handleRemoveAfterImage}
							onAcceptProposal={handleAcceptProposal}
							onRejectProposal={handleRejectProposal}
						/>
						{isSectionAllowed("activity") && (
							<ActivityTab projectId={project.id} activities={activities} />
						)}
					</div>
				)}
				{activeTab === "team" && isSectionAllowed("team") && (
					<TeamTab
						project={project}
						projectId={project.id}
						contractors={contractors}
						onRefresh={refreshContractors}
					/>
				)}
				{activeTab === "plan" && isSectionAllowed("plan") && (
					<div className="space-y-8">
						<TasksTab
							projectId={project.id}
							tasks={tasks}
							milestones={milestones}
							onRefresh={refreshTasks}
							userType={userType}
							userId={userId}
						/>
						<MilestonesTab
							projectId={project.id}
							milestones={milestones}
							tasks={tasks}
							onRefresh={refreshMilestones}
							onRefreshTasks={refreshTasks}
							releaseErrors={releaseErrors}
							userType={userType}
							userId={userId}
							onReleasePayment={handleReleaseMilestonePayment}
							releaseLoading={releaseLoading}
						/>
					</div>
				)}
				{activeTab === "payments" && isSectionAllowed("payments") && (
					<PaymentsTab
						projectId={project.id}
						transactions={paymentTransactions}
						summary={paymentSummary}
					/>
				)}
				{activeTab === "boq" && isSectionAllowed("boq") && (
					<div className="space-y-8">
						{userType !== "contractor" && (
							<CostsTab
								projectId={project.id}
								costs={costs}
								budgetMax={project.budgetMax}
								onRefresh={refreshCosts}
							/>
						)}
						<DesignerBoqTab projectId={project.id} />
						{(userType === "designer" || userType === "contractor") && (
							<ProjectVendorRatingSection projectId={project.id} />
						)}
					</div>
				)}
				{activeTab === "documents" && isSectionAllowed("documents") && (
					<DocumentsTab projectId={project.id} />
				)}
				{activeTab === "materials" && isSectionAllowed("materials") && (
					<div className="space-y-6">
						<div className="flex justify-between items-end flex-wrap gap-4">
							<div>
								<Eyebrow style={{ fontSize: 9 }}>
									MATERIALS · {shortlist.length} SELECTED
								</Eyebrow>
								<h2
									style={{
										fontFamily: "var(--font-display)",
										fontWeight: 500,
										fontSize: 34,
										lineHeight: 1.05,
										letterSpacing: "-0.02em",
										color: "var(--color-dashboard-text)",
										margin: "8px 0 0",
									}}
								>
									Chosen with{" "}
									<ItalicAccent
										style={{
											fontSize: 34,
											color: "var(--color-dashboard-accent-display)",
										}}
									>
										intention
									</ItalicAccent>
									.
								</h2>
							</div>
							<StudioButton
								variant="secondary"
								size="md"
								icon={Plus}
								onClick={() => navigate(`/catalog`)}
							>
								Browse Catalog
							</StudioButton>
						</div>

						{shortlist.length === 0 ? (
							<FloatPanel elevation={1} padding={32}>
								<div className="flex flex-col items-center gap-4 text-center">
									<Layers className="h-10 w-10 text-[var(--color-dashboard-muted)]/40" />
									<p className="text-sm text-[var(--color-dashboard-muted)] font-medium max-w-sm">
										No materials shortlisted yet. Browse our curated catalog to
										find and add premium materials.
									</p>
									<StudioButton
										variant="primary"
										size="md"
										onClick={() => navigate(`/catalog`)}
									>
										Browse Catalog
									</StudioButton>
								</div>
							</FloatPanel>
						) : (
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
								{shortlist.map((entry, idx) => {
									// derive status: approved, selected, awaiting
									const statusKey =
										idx % 3 === 0
											? "awaiting"
											: idx % 3 === 1
												? "approved"
												: "selected";
									const statusPills: Record<
										string,
										{ tone: PillTone; label: string }
									> = {
										awaiting: { tone: "awaiting", label: "Awaiting approval" },
										approved: { tone: "done", label: "Approved" },
										selected: { tone: "active", label: "Selected" },
									};
									const status = statusPills[statusKey];

									return (
										<FloatPanel
											key={entry.id}
											elevation={1}
											padding={0}
											style={{ overflow: "hidden" }}
										>
											<div
												style={{
													position: "relative",
													paddingBottom: "75%",
													background: "var(--color-dashboard-surface)",
												}}
											>
												{entry.product?.images?.[0] ? (
													<img
														src={entry.product.images[0]}
														alt={entry.product.name}
														style={{
															position: "absolute",
															left: 0,
															top: 0,
															width: "100%",
															height: "100%",
															objectFit: "cover",
														}}
													/>
												) : (
													<div
														style={{
															position: "absolute",
															left: 0,
															top: 0,
															width: "100%",
															height: "100%",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
														}}
													>
														<Layers className="h-10 w-10 text-[var(--color-dashboard-muted)]/30" />
													</div>
												)}
												<div
													style={{
														position: "absolute",
														left: 12,
														top: 12,
														zIndex: 2,
													}}
												>
													<Pill tone={status.tone} dot={true}>
														{status.label}
													</Pill>
												</div>
											</div>
											<div style={{ padding: 18 }}>
												<Eyebrow style={{ fontSize: 8 }}>
													{entry.product?.brand?.name || "CURATED"}
												</Eyebrow>
												<h3
													style={{
														fontFamily: "var(--font-display)",
														fontWeight: 600,
														fontSize: 16,
														color: "var(--color-dashboard-ink-soft)",
														marginTop: 6,
													}}
													className="line-clamp-1"
												>
													{entry.product?.name || "Unknown Product"}
												</h3>
												{entry.variant && (
													<p className="text-xs text-[var(--color-dashboard-muted)] mt-1 font-mono">
														{Object.values(
															entry.variant.variantAttributes,
														).join(" / ")}
													</p>
												)}
												{entry.quantity && (
													<p className="text-xs text-dashboard-muted-alt mt-2 font-medium">
														Quantity: {entry.quantity}
													</p>
												)}

												<div className="flex justify-between items-center mt-4 pt-3 border-t border-dashboard-divider">
													<div className="font-mono text-xs text-[var(--color-dashboard-muted)]">
														14 DAYS LEAD TIME
													</div>
													<button
														type="button"
														onClick={async () => {
															const result = await removeFromShortlist(
																project?.id,
																entry.id,
															);
															if (result.success) {
																setShortlist((prev) =>
																	prev.filter((e) => e.id !== entry.id),
																);
																toast.success("Removed from shortlist");
															}
														}}
														style={{
															background: "transparent",
															border: 0,
															cursor: "pointer",
															color: "var(--color-destructive)",
															display: "flex",
															alignItems: "center",
															padding: 6,
														}}
													>
														<Trash2 className="h-4 w-4" />
													</button>
												</div>
											</div>
										</FloatPanel>
									);
								})}
							</div>
						)}
					</div>
				)}
				{activeTab === "messages" && isSectionAllowed("messages") && (
					<div className="space-y-8">
						{/* This gate is client-side on purpose, and deliberately not
						    mirrored on the server the way getProjectActivity and
						    listProjectShareLinks are. It is a product gate -- no
						    contractor DM surface exists yet -- not a security boundary:
						    contractors are DM participants by design, and R12 in
						    docs/brainstorms/2026-08-03-contractor-integration-requirements.md
						    is an accepted requirement to surface DMs for them on this same
						    endpoint. The reasoning, including what refusing them
						    server-side would fail to close, is in
						    docs/plans/2026-09-24-0700-refactor-project-detail-tabs-plan.md. */}
						{isSectionAllowed("directMessages") && (
							<ProjectDirectMessagesPanel projectId={project.id} />
						)}
						<CommentsPanel
							subjectId={project.id}
							adapter={projectCommentSubjectAdapter}
						/>
					</div>
				)}
			</div>
		</div>
	);
}
